import {
  AttributionControl,
  GeoJSONSource,
  ILngLatLike,
  LngLatBounds,
  Map as MapLibreMap,
  MapGeoJSONFeature,
  MapMouseEvent,
  MapStyleImageMissingEvent,
  NavigationControl,
  Popup,
  ScaleControl,
} from "maplibre-gl"
import type { MapLayerEventType } from "maplibre-gl"
import CustomMapLibreGlDirections, {
  PolylineArray,
  RouteLineDragEndEvent,
} from "@/components/maps/CustomMapLibreGlDirections"
import {
  fingerprintPolylines,
  trackRouteEvent,
} from "@/components/maps/routeTelemetry"
import { waypointHoverPopupHtml } from "@/components/maps/waypointHoverPopup"
import {
  analyzeOrderRoutePopupHtml,
  redrawEveryMinute,
  routeInfoPopupHtml,
} from "@/components/maps/routeInfoPopup"
import { popupShift } from "@/components/maps/popupPlacement"
import { AvoidanceAreasManager } from "@/components/maps/AvoidanceAreasManager"
import type { RoutingProvider } from "@/reducers/serviceConfigSlice"
import { sumRouteMetrics, svgToCanvas } from "@/components/maps/mapUtils"
import { isMobile } from "@/utils/detectDevice"
import {
  LayerSpecification,
  MapLibreGlDirectionsWaypointEvent,
  Point,
} from "@maplibre/maplibre-gl-directions"
import {
  Action,
  ACTIONS,
  stopActionLabel,
  findCurrentTarget,
  isStopActive,
  isStopLate,
  isStopPending,
  Route2g,
  RouteStop,
} from "@/reducers/routes2gSlice"
import AdHocCommands, {
  AvoidType,
  CarRouteConfig,
  LoadType,
} from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import { RouteInfo, TrafficSection } from "@/components/maps/RouteView"
import * as polyline from "@mapbox/polyline"
import PulsingDot from "./PulsingDot"
import { debounce } from "lodash"
import { AvoidanceItems } from "@/lib/avoidanceItemsParser"
import DriverIcon from "@/components/maps/DriverIcon" // @ts-ignore
import { URLShieldRenderer } from "@americana/maplibre-shield-generator"
import * as Poi from "./Poi"
import { attachRegionBorders } from "./regionBorders"
import { parseImageName } from "./shields"
import { clearRouteHandlesFromPassthroughStops } from "@/hooks/useTemporaryRoute"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import PlanRouteParser from "@/lib/planRouteParser"
import type { DetailedLocation } from "@/hooks/useReverseGeocode"
import { getRouteColorsFromLabel } from "@/lib/routeColors"
import {
  FUEL_STATION_ICONS,
  FuelStation,
  fuelStationIconImageId,
  normalizeFuelStationColor,
} from "@/lib/fuelStations"
import {
  AUTOHOF_ICON_IMAGE_ID,
  AUTOHOF_ICON_URL,
  Autohof,
} from "@/lib/autohofs"
import {
  ROAD_SHIELD_LAYER_IDS,
  getAboveRouteBeforeId,
  raiseLayersAboveRouteLines,
} from "@/components/maps/aboveRouteLayerOrder"
import type { Location } from "@/reducers/locationsSlice"
import i18n from "@/lib/i18n/i18n"
import TrafficIncidentsLayer, {
  TRAFFIC_INCIDENTS_LAYER_ID,
} from "@/components/maps/TrafficIncidentsLayer"
import {
  ROUTE_CONGESTION_COLOR,
  ROUTE_TRAFFIC_OFF,
  RouteTrafficSettings,
  TrafficIncident,
  TrafficRange,
  routeTrafficFingerprint,
  shownTrafficDelay,
} from "@/components/maps/routeTraffic"
import {
  STOP_NUMBER_LAYER_ID,
  addMissingStopNumberBadge,
  stopNumberLayer,
} from "@/components/maps/stopNumberBadge"
import {
  filterIncidentsByType,
  routeIncidents,
} from "@/components/maps/trafficIncidents"
import {
  PAST_STOP_LEFT_IMAGE_ID,
  PAST_STOP_LEFT_SVG,
  PAST_STOP_SKIPPED_IMAGE_ID,
  PAST_STOP_SKIPPED_SVG,
  PAST_STOPS_LAYER_ID,
  PAST_STOPS_SOURCE_ID,
  PAST_TRAIL_CASING_LAYER_ID,
  PAST_TRAIL_LAYER_ID,
  PAST_TRAIL_SOURCE_ID,
  type PastStopsOverlay,
  getBelowRouteLinesBeforeId,
  pastStopPopupContent,
  pastStopsOverlayPoints,
  pastTrailColor,
  pastTrailFeatureCollection,
} from "@/components/maps/pastStopsLayers"
import {
  pastStopsFeatureCollection,
  type PastStopFeatureProps,
} from "@/lib/pastStops"

export type { PastStopsOverlay }

type PastStopMouseEvent = MapMouseEvent & {
  features?: MapGeoJSONFeature[] | undefined
}

export const shieldPredicate: any = (imageID: string) =>
  imageID && imageID.startsWith("shield")

export const networkPredicate: any = (network: string) =>
  // On recreational route relations, network=* indicates the network's scope,
  // not the network itself.
  // https://github.com/osm-americana/openstreetmap-americana/issues/94
  !/^[lrni][chimpw]n$/.test(network)

export const routeParser: any = {
  parse: (id: string) => {
    return parseImageName(id)
  },
  format: (network: string, ref: string, name: string) =>
    `shield\n${network}\n${ref}\n${name}\n`,
}

export type MapStyleEntry = {
  name: string
  url: string
  darkUrl: string
  tilesProvider: "osm" | "maptiler"
}

export type MapStyle = "default" | "satellite" | "americana"
export type MapStyles = Record<MapStyle, MapStyleEntry>

export type MapConfig = {
  style: MapStyle
  center: [number, number]
  zoom: number
}

// Style pickers show `maps.changeMapStyle.styles.<id>`, not `name`.
export const MAP_STYLES: MapStyles = {
  default: {
    name: "Default",
    url: "/mapStyle.json",
    darkUrl: "/mapStyleDark.json",
    tilesProvider: "osm",
  },
  americana: {
    name: "Roads",
    url: "/americana.json",
    darkUrl: "/mapStyle.json",
    tilesProvider: "osm",
  },
  satellite: {
    name: "Satellite",
    url: "https://api.maptiler.com/maps/hybrid/style.json",
    darkUrl: "https://api.maptiler.com/maps/hybrid/style.json",
    tilesProvider: "maptiler",
  },
} as const

export const DEFAULT_MAP_CONFIG: MapConfig = {
  style: "default",
  zoom: 9,
  center: [50, 20],
}

export const MAPTILER_KEY = import.meta.env.REACT_APP_MAPTILER_KEY
const FUEL_STATIONS_SOURCE_ID = "fuel-stations-source"
const FUEL_STATIONS_LAYER_ID = "fuel-stations-layer"
const FUEL_STATIONS_ICON_LAYER_ID = "fuel-stations-icon-layer"
const FUEL_STATIONS_LAYER_IDS = [
  FUEL_STATIONS_LAYER_ID,
  FUEL_STATIONS_ICON_LAYER_ID,
]
const FUEL_STATION_ICON_SIZE_PX = 26
const AUTOHOFS_SOURCE_ID = "autohofs-source"
const AUTOHOFS_LAYER_ID = "autohofs-layer"
// The shields come first, so they end up below the POI markers - a covered
// station dot is worse than a covered road number. The past stops of the
// current order go last: they are about this driver, the POIs are not.
const ABOVE_ROUTE_LAYER_IDS = [
  ...ROAD_SHIELD_LAYER_IDS,
  FUEL_STATIONS_LAYER_ID,
  FUEL_STATIONS_ICON_LAYER_ID,
  AUTOHOFS_LAYER_ID,
  TRAFFIC_INCIDENTS_LAYER_ID,
  PAST_STOPS_LAYER_ID,
]

export default class MapManager {
  private updateGeneration = 0
  // Telemetry: when the current room's map was opened and how many times its
  // stored route has been drawn since
  private roomOpenedAt = Date.now()
  private storedRouteDrawsForRoom = 0
  private mapContainer: HTMLDivElement
  private mapInstance: MapLibreMap
  private mapConfig: MapConfig
  /** Set once `clearMap` has torn the map down. A destroyed MapLibre map
   * keeps its object but drops its style, so callers can't ask the map
   * itself whether it is still usable — `isStyleLoaded()` also goes false
   * while tiles are merely loading, which is not the same thing. */
  private destroyed = false
  private detachRegionBorders: (() => void) | null = null
  private distancePopup: Popup | null = null
  private hoverPopup: Popup | null = null
  private directions: CustomMapLibreGlDirections | null = null
  private isSharedMap: boolean = false
  private isAlertMap: boolean = false
  private isAnalyzeOrderMap: boolean = false
  private isReadOnlyMap: boolean = false
  private avoidanceAreasManager: AvoidanceAreasManager | null = null
  private routeJid: string | null = null
  private isUpdatingRoute: boolean = false
  private routeInfo: RouteInfo | null = null
  private sharedRoute: RouteInfo | null = null
  private sharedStops: Route2g | null = null
  private polylines: string[] | null = null
  private sections: TrafficSection[] | null = null
  // HERE traffic colors and incident icons (flag `route-traffic`)
  private routeTraffic: RouteTrafficSettings = ROUTE_TRAFFIC_OFF
  // Number badges on the stop pins (flag `map-stop-numbers`)
  private showStopNumbers = false
  private redrawRouteInfoPopup: (() => void) | null = null
  private trafficIncidentsLayer: TrafficIncidentsLayer | null = null
  private currentLocation: ILngLatLike | null = null
  private currentLocationBearing: number | undefined = undefined
  private currentActivity: string | undefined = undefined
  private route: Route2g | null = null
  private onNewRoute: ((route: RouteInfo) => void) | undefined
  private onClearRoute: (() => void) | undefined
  private onRouteStopsUpdate:
    | ((route: Route2g["stops"]) => Promise<void>)
    | undefined
  private onGetAvoidanceItems:
    | (() => Promise<AvoidanceItems | undefined>)
    | undefined
  private distancePopupDismissed: boolean = false
  private isDarkMode: boolean = false
  private shouldShowEntireRoute: boolean = true
  private temporaryAvoidOptions: AvoidType[] = []
  private temporaryLoadType: LoadType[] = []
  private temporaryCarConfig: CarRouteConfig = {}
  private shouldShowHoverPopup: boolean = true
  private shieldRenderer: URLShieldRenderer
  private onRouteDragChange: ((isDragging: boolean) => void) | undefined
  private selectedRouteLabel: string | undefined = undefined
  private fuelStations: FuelStation[] = []
  private fuelStationsVisible: boolean = false
  private fuelStationsVisibleCategories: Set<string> = new Set()
  private fuelStationsPopup: Popup | null = null
  private fuelStationsEventHandlers: {
    event: keyof Pick<
      MapLayerEventType,
      "click" | "mouseenter" | "mousemove" | "mouseleave"
    >
    layerId: string
    handler: (e: any) => void
  }[] = []
  private fuelStationIconsPromise: Promise<void> | null = null
  private fuelStationIconsMapInstance: MapLibreMap | null = null
  private fuelStationsUpdateQueued = false
  private autohofs: Autohof[] = []
  private autohofsVisible: boolean = false
  private autohofsEventHandlers: {
    event: keyof Pick<
      MapLayerEventType,
      "click" | "mouseenter" | "mousemove" | "mouseleave"
    >
    handler: (e: any) => void
  }[] = []
  private autohofIconPromise: Promise<void> | null = null
  private autohofIconMapInstance: MapLibreMap | null = null
  private autohofsUpdateQueued = false
  /** Stops of the current order the driver is done with, and the path they
   * drove. Kept apart from the route: redrawing or clearing the route leaves
   * it alone, and it is redrawn on every new map instance (style change). */
  private pastStopsOverlay: PastStopsOverlay | null = null
  private pastStopsEventHandlers: {
    event: keyof Pick<
      MapLayerEventType,
      "click" | "mouseenter" | "mousemove" | "mouseleave"
    >
    handler: (e: PastStopMouseEvent) => void
  }[] = []
  private pastStopsPopup: Popup | null = null
  /** A clicked popup stays open after the pointer leaves the marker. */
  private pastStopsPopupPinned = false
  private pastStopsUpdateQueued = false
  private routingProvider: RoutingProvider | undefined = undefined

  constructor({
    mapContainer,
    mapConfig,
    isSharedMap,
    isAlertMap,
    isAnalyzeOrderMap = false,
    isReadOnlyMap = false,
    sharedRoute = null,
    sharedStops = null,
    routeJid = null,
    onNewRoute,
    onClearRoute,
    onRouteStopsUpdate,
    onGetAvoidanceItems,
    currentLocation = null,
    currentLocationBearing = null,
    currentActivity = null,
    isDarkMode,
    routeInfo = null,
    temporaryAvoidOptions,
    temporaryLoadType,
    temporaryCarConfig,
    shouldShowEntireRoute = true,
    routingProvider,
  }: {
    mapContainer: HTMLDivElement
    mapConfig?: MapConfig
    currentLocation?: ILngLatLike | null
    currentLocationBearing?: number | null
    currentActivity?: string | null
    isSharedMap?: boolean
    isAlertMap?: boolean
    isAnalyzeOrderMap?: boolean
    isReadOnlyMap?: boolean
    sharedRoute?: RouteInfo | null
    sharedStops?: Route2g | null
    routeJid?: string | null
    onNewRoute?: (route: RouteInfo) => void | null
    onClearRoute?: () => void | null
    onRouteStopsUpdate?: (route: Route2g["stops"]) => Promise<void>
    onGetAvoidanceItems?: () => Promise<AvoidanceItems | undefined>
    isDarkMode: boolean
    routeInfo?: RouteInfo | null
    temporaryAvoidOptions?: AvoidType[]
    temporaryLoadType?: LoadType[]
    temporaryCarConfig?: CarRouteConfig
    shouldShowEntireRoute?: boolean
    routingProvider?: RoutingProvider
  }) {
    this.mapContainer = mapContainer
    this.mapConfig = mapConfig ?? DEFAULT_MAP_CONFIG
    this.routeJid = routeJid
    this.isSharedMap = isSharedMap ?? false
    this.isAlertMap = isAlertMap ?? false
    this.isAnalyzeOrderMap = isAnalyzeOrderMap
    this.isReadOnlyMap = isReadOnlyMap
    this.sharedRoute = sharedRoute
    this.sharedStops = sharedStops
    this.onNewRoute = onNewRoute
    this.onClearRoute = onClearRoute
    this.onRouteStopsUpdate = onRouteStopsUpdate
    this.onGetAvoidanceItems = onGetAvoidanceItems
    this.currentLocation = currentLocation ?? null
    this.currentLocationBearing = currentLocationBearing ?? undefined
    this.currentActivity = currentActivity ?? undefined
    this.isDarkMode = isDarkMode
    this.routeInfo = routeInfo
    this.temporaryAvoidOptions = temporaryAvoidOptions || []
    this.temporaryLoadType = temporaryLoadType || []
    this.temporaryCarConfig = temporaryCarConfig || {}
    this.shouldShowEntireRoute = shouldShowEntireRoute
    this.routingProvider = routingProvider

    this.mapInstance = new MapLibreMap({
      container: this.mapContainer,
      style: this.getMapStyleUrl(this.mapConfig.style, this.isDarkMode),
      center: this.mapConfig.center,
      zoom: this.mapConfig.zoom,
      attributionControl: false, // We have custom attribution with map provider information
    })

    this.initializeMap()
    this.avoidanceAreasManager =
      this.onGetAvoidanceItems === null || this.isSharedMap
        ? null
        : new AvoidanceAreasManager(this.mapInstance, this.onGetAvoidanceItems!)
  }

  private handleMapLoaded = () => {
    if (!this.isSharedMap) {
      this.avoidanceAreasManager?.getAvoidanceItems()
    }
    this.updateFuelStationsLayer()
    this.updateAutohofsLayer()
    this.updatePastStopsLayers()
    this.createActionIcons()
    this.initializeDirections()
    // After the directions, so the incident icons' cursor handler runs last
    this.updateTrafficIncidents()
    // The directions layers were just appended on top of the station markers
    // and of the road number shields.
    this.raiseLayersAboveRouteLines()
    if (!this.mapInstance.hasImage("driver-icon")) {
      this.mapInstance.addImage("driver-icon", DriverIcon(this.mapInstance), {
        pixelRatio: 2,
      })
    }

    if (!this.isSharedMap && this.currentLocation) {
      this.createCurrentLocationIndicator()
    }
  }

  private addHoverHandlers() {
    if (!this.mapInstance) return

    // Remove existing handlers first to avoid duplicates
    this.mapInstance.off(
      "mousemove",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseEnter,
    )
    this.mapInstance.off(
      "mouseleave",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseLeave,
    )
    this.mapInstance.off(
      "mousedown",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseDown,
    )
    this.mapInstance.off(
      "mouseup",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseUp,
    )

    this.mapInstance.on(
      "mousemove",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseEnter,
    )
    this.mapInstance.on(
      "mouseleave",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseLeave,
    )
    this.mapInstance.on(
      "mousedown",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseDown,
    )
    this.mapInstance.on(
      "mouseup",
      "maplibre-gl-directions-waypoint-icon",
      this.handleIconMouseUp,
    )
  }

  public setShouldShowHoverPopup(value: boolean) {
    this.shouldShowHoverPopup = value
  }

  private handleIconMouseDown = () => {
    this.shouldShowHoverPopup = false
  }

  private handleIconMouseUp = () => {
    this.shouldShowHoverPopup = true
  }

  private getActionByValue = (value: string | undefined): Action => {
    const action = ACTIONS.find(
      (action) => action.value.toLowerCase() === value,
    )
    return action ?? ACTIONS.find((action) => action.value === "other")!
  }

  private handleIconMouseEnter = (
    e: MapMouseEvent & { features?: MapGeoJSONFeature[] | undefined },
  ) => {
    if (e.features && e.features.length > 0 && this.shouldShowHoverPopup) {
      const feature = e.features[0]

      // Remove existing hover popup if any
      if (this.hoverPopup) {
        this.hoverPopup.remove()
        this.hoverPopup = null
      }
      const action = this.getActionByValue(feature.properties.action)
      // Create and show popover
      const { coordinates } = feature.geometry as unknown as Point
      const text = waypointHoverPopupHtml({
        actionLabel: stopActionLabel(i18n.t, action.value) ?? action.label,
        address: feature.properties.address,
        companyName: feature.properties.companyName,
        isAlertMap: this.isAlertMap,
      })

      this.hoverPopup = new Popup({
        closeButton: false,
        closeOnClick: false,
        focusAfterOpen: false,
        className: "!max-w-[300px]",
        // No fixed anchor: with the client name and address the popup is
        // wide enough to be cut off by the map edge, so let MapLibre flip it.
        offset: 10,
      })
        .setLngLat(coordinates as [number, number])
        .setHTML(
          `
          <div class="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg">
            ${text}
          </div>
        `,
        )
        .addTo(this.mapInstance)
    }
  }

  private handleIconMouseLeave = () => {
    // Hide the hover popup
    if (this.hoverPopup) {
      this.hoverPopup.remove()
      this.hoverPopup = null
    }
  }

  public resetHoverState = () => {
    // Hide the hover popup
    if (this.hoverPopup) {
      this.hoverPopup.remove()
      this.hoverPopup = null
    }

    // Re-establish hover handlers after a brief delay
    setTimeout(() => {
      this.addHoverHandlers()
    }, 50)
  }

  public updateMap({
    routeJid,
    onNewRoute,
    onClearRoute,
    onRouteStopsUpdate,
    currentLocation,
    isDarkMode,
  }: {
    currentLocation: ILngLatLike | undefined
    routeJid: string
    onNewRoute: (route: RouteInfo) => void
    onClearRoute: () => void
    onRouteStopsUpdate: (route: Route2g["stops"]) => Promise<void>
    isDarkMode: boolean
  }) {
    this.routeJid = routeJid
    this.updateGeneration++
    this.roomOpenedAt = Date.now()
    this.storedRouteDrawsForRoom = 0
    if (this.distancePopup) {
      this.distancePopup.off("close", this.handleDistancePopupClose)
      this.distancePopup.remove()
    }
    this.removeAllPopups()
    this.clearRoute()
    this.onNewRoute = onNewRoute
    this.onClearRoute = onClearRoute
    this.onRouteStopsUpdate = onRouteStopsUpdate
    this.currentLocation = currentLocation ?? null
    if (this.currentLocation) {
      this.setCurrentLocation(
        this.currentLocation,
        this.currentActivity,
        this.currentLocationBearing,
      )
    }
    this.isDarkMode = isDarkMode
    if (this.isAnalyzeOrderMap) {
      this.directions?.clear()
    }
  }

  private initializeDirections() {
    this.directions = new CustomMapLibreGlDirections(this.mapInstance, {
      requestOptions: {
        annotations: "congestion_numeric",
      },
      layers: this.createLayerIcons(),
      refreshOnMove: true,
      sensitiveWaypointLayers: [
        "maplibre-gl-directions-waypoint-driver-icon",
        "maplibre-gl-directions-waypoint-icon",
      ],
      showCurrentLocation: !!this.currentLocation,
      stops: this.route?.stops,
    })

    this.directions?.setRouteJid(this.routeJid)

    // On mobile and when map is shared we are blocking route dragging

    this.directions.interactive = !isMobile && !this.isSharedMap
    this.directions.setRouteCallback(this.fetchNewRoute)
    if (this.onRouteDragChange) {
      this.directions.setOnRouteDragChange(this.onRouteDragChange)
    }
    this.directions.on("removewaypoint", this.removePassthroughWaypoint)
    this.directions.on("movewaypoint", this.moveWaypoint)
    if (this.mapContainer) {
      this.mapContainer.addEventListener(
        "routelinedragend",
        this.addNewPassthroughWaypoint,
      )
    }
    this.fetchRoute()
    this.addHoverHandlers()
  }

  private createCurrentLocationIndicator() {
    // Add the pulsing dot image if it doesn't exist
    if (!this.mapInstance.hasImage("pulsing-dot")) {
      this.mapInstance.addImage("pulsing-dot", PulsingDot(this.mapInstance), {
        pixelRatio: 2,
      })
    }

    // Add the source and layer if they don't exist
    if (!this.mapInstance.getSource("location-point")) {
      this.mapInstance.addSource("location-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: this.currentLocation as number[],
              },
              properties: {
                bearing: this.currentLocationBearing,
                activity: this.currentActivity,
              },
            },
          ],
        },
      })
      this.mapInstance.addLayer({
        id: "location-point",
        type: "symbol",
        source: "location-point",
        layout: {
          "icon-image": "pulsing-dot",
        },
      })
    }
  }

  addNewPassthroughWaypoint = async (event: RouteLineDragEndEvent) => {
    const waypointIndex = event.detail.waypointIndex
    try {
      // Set the flag to indicate we're starting an update
      this.isUpdatingRoute = true
      const stops = this.enrichStops(this.route?.stops ?? null)
      if (stops) {
        const isPreviousNearby =
          waypointIndex > 0 &&
          stops[waypointIndex - 1].proximity_status &&
          !["arriving", "far"].includes(
            stops[waypointIndex - 1].proximity_status!,
          )

        const shouldChangeIndex = !(
          this.isAnalyzeOrderMap && !this.currentLocation
        )
        const changeIndexBy = shouldChangeIndex
          ? -1 +
            (stops || []).filter((s) => s.proximity_status === "left").length
          : 0

        const index = isPreviousNearby
          ? event.detail.waypointIndex
          : event.detail.waypointIndex + changeIndexBy

        const newStop = {
          location: {
            formattedAddress: `${event.detail.waypoint[1]},${event.detail.waypoint[0]}`,
          },
          addressUpdated: true,
          action: "passthrough",
        } as unknown as RouteStop
        // Insert the new stop at the specified index instead of appending to the end
        const updated = [
          ...stops.slice(0, index),
          newStop,
          ...stops.slice(index),
        ]
        try {
          await this.onRouteStopsUpdate?.(updated)
        } catch (e) {
          this.fetchRoute(true)
        }
      }
    } finally {
      this.isUpdatingRoute = false
    }
  }

  fetchRoute = async (forceUpdate: boolean = false) => {
    // Capture the generation at the start of the operation
    const currentGeneration = this.updateGeneration
    if (
      this.isSharedMap &&
      this.sharedRoute &&
      this.directions &&
      this.sharedStops
    ) {
      const polylines = this.sharedRoute.routes[0].legs.map(
        ({ polyline }) => polyline,
      )
      const sections = this.sharedRoute.sections || []

      this.routeInfo = this.sharedRoute
      this.sections = sections
      this.polylines = polylines
      await this.directions.setInitialRoute(
        polylines,
        sections,
        this.sharedStops,
      )
      if (this.shouldShowEntireRoute) {
        this.showEntireRoute()
      }
    } else {
      try {
        let routeInfo: RouteInfo | null = null
        if (this.isSharedMap) {
          routeInfo = this.sharedRoute
        } else if (this.isAnalyzeOrderMap && this.route?.stops) {
          routeInfo = this.routeInfo
          // const result = await AdHocCommands.getOrderPolylines(
          //   window.XMPP!,
          //   this.routeJid!,
          //   this.route?.stops,
          // )
          // const routeData = OrderAnalyzeRouteParser.parse(result)
          // routeInfo = routeData?.route ?? null
        } else if (this.routeJid && !this.isAnalyzeOrderMap) {
          const result = await AdHocCommands.getRoutePolylines(
            window.XMPP!,
            this.routeJid,
          )
          routeInfo = RoutePolylinesParser.parse(result)?.route ?? null
        }
        if (currentGeneration !== this.updateGeneration) {
          console.log(
            "MapManager: Stale fetchRoute call ignored due to update.",
          )
          return // Silently exit
        }
        if (routeInfo && this.directions) {
          const polylines = routeInfo.routes[0].legs.map(
            ({ polyline }) => polyline,
          )
          const sections = routeInfo.sections || []

          const polylinesChanged =
            JSON.stringify(this.polylines) !== JSON.stringify(polylines)
          const sectionsChanged =
            JSON.stringify(this.sections) !== JSON.stringify(sections)
          const trafficChanged =
            routeTrafficFingerprint(this.routeInfo) !==
            routeTrafficFingerprint(routeInfo)

          if (
            polylinesChanged ||
            sectionsChanged ||
            trafficChanged ||
            forceUpdate
          ) {
            this.storedRouteDrawsForRoom++
            trackRouteEvent("MapStoredRouteDrawn", {
              routeJid: this.routeJid,
              drawForRoom: this.storedRouteDrawsForRoom,
              msSinceRoomOpened: Date.now() - this.roomOpenedAt,
              polylinesChanged,
              sectionsChanged,
              trafficChanged,
              forceUpdate,
              legCount: polylines.length,
              fingerprint: fingerprintPolylines(polylines),
              previousFingerprint: fingerprintPolylines(this.polylines),
              isAnalyzeOrderMap: this.isAnalyzeOrderMap,
              // false on a first draw = the map did not zoom to the route
              willShowEntireRoute: !!this.route && this.shouldShowEntireRoute,
            })
            this.onNewRoute?.(routeInfo)
            this.routeInfo = routeInfo
            this.sections = sections
            this.polylines = polylines
            if (this.route) {
              await this.directions.setInitialRoute(
                polylines,
                sections,
                this.route,
                this.legTraffic(routeInfo),
              )
              if (this.shouldShowEntireRoute) {
                this.showEntireRoute()
                this.shouldShowEntireRoute = false
              }
              // Re-establish hover handlers after route update
              setTimeout(() => {
                this.addHoverHandlers()
              }, 100)
              // Update route colors after route is rendered
              setTimeout(() => {
                this.updateRouteLayerColors()
              }, 150)
            } else {
              this.showCurrentLocationOrPastStops()
            }
            if (!forceUpdate) {
              this.distancePopupDismissed = false
            }
            this.initializeDistancePopup()
            this.updateTrafficIncidents()
          }
        } else {
          const hasActiveStops =
            this.route &&
            this.route.stops &&
            (this.route.stops || []).filter(
              (s) => s.proximity_status !== "left",
            ).length
          if (!hasActiveStops) {
            this.directions?.clearRoute()
            this.initializeDistancePopup()
            this.onClearRoute?.()
          } else if (
            this.directions &&
            forceUpdate &&
            this.route &&
            this.sections &&
            this.polylines
          ) {
            await this.directions.setInitialRoute(
              this.polylines,
              this.sections,
              this.route,
              this.legTraffic(this.routeInfo),
            )
            // Update route colors after route is rendered
            setTimeout(() => {
              this.updateRouteLayerColors()
            }, 150)
          }
        }
      } catch (e) {
        this.directions?.clear()
        this.showCurrentLocation()
        console.error("debug MapView: Error in fetchRoute:", e)
      }
    }
  }

  fetchNewRoute = async (
    waypoints: [number, number][],
    stops: Route2g["stops"] | undefined,
    retryCount = 0,
  ): Promise<PolylineArray> => {
    try {
      const currentGeneration = this.updateGeneration
      if (this.isUpdatingRoute) {
        // Limit retries to 3 attempts
        if (retryCount >= 3) {
          return []
        }

        // Wait for 300ms before retrying
        await new Promise((resolve) => setTimeout(resolve, 300))
        return this.fetchNewRoute(waypoints, stops, retryCount + 1)
      }
      if (this.isAnalyzeOrderMap && this.routeJid === "on-page") {
        console.log("[DEBUG] Fetching order polylines via XMPP AdHocCommand")
        if (stops) {
          const sanitizedStops = clearRouteHandlesFromPassthroughStops(stops)
          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            `on-page@${window.XMPP!.options.domain}`,
            sanitizedStops,
            {
              ...this.temporaryCarConfig,
              avoid: this.temporaryAvoidOptions,
              loadType: this.temporaryLoadType,
            },
          )
          const { route } = PlanRouteParser.parse(result) || {}
          console.log("[DEBUG] ", sanitizedStops)
          console.log(
            "[DEBUG] ",
            route?.routes?.[0].legs.map(({ polyline }) => polyline),
          )
          return route?.routes?.[0].legs.map(({ polyline }) => polyline) ?? []
        }
      } else if (this.isAnalyzeOrderMap && this.routeJid !== "on-page") {
        console.log("[DEBUG] Fetching order polylines via XMPP AdHocCommand")
        if (stops && this.currentLocation) {
          const currentLocation = this.currentLocation as number[]
          const currentLocationStop = {
            addressUpdated: true,
            location: {
              formattedAddress: `${currentLocation?.[1]},${currentLocation?.[0]}`,
            },
          }
          const sanitizedStops = clearRouteHandlesFromPassthroughStops(stops)
          const carConfig =
            (await XmppPubSubApi.fetchCarConfig(
              window.XMPP!,
              this.routeJid!,
            )) ?? {}

          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            this.routeJid!,
            [currentLocationStop, ...sanitizedStops],
            {
              ...carConfig,
              avoid: this.temporaryAvoidOptions,
              loadType: this.temporaryLoadType,
            },
          )
          const { route } = PlanRouteParser.parse(result) || {}
          console.log("[DEBUG] ", [currentLocationStop, ...sanitizedStops])
          console.log(
            "[DEBUG] ",
            route?.routes?.[0].legs.map(({ polyline }) => polyline),
          )
          return route?.routes?.[0].legs.map(({ polyline }) => polyline) ?? []
        }
      } else {
        const result = await AdHocCommands.getNewRoutePolylines(
          window.XMPP!,
          this.routeJid!,
          waypoints,
          this.isAnalyzeOrderMap ? this.temporaryAvoidOptions : undefined,
          this.isAnalyzeOrderMap ? this.temporaryLoadType : undefined,
        )

        if (currentGeneration !== this.updateGeneration) {
          console.log(
            "MapManager: Stale fetchNewRoute call ignored after fetch.",
          )
          return []
        }

        const route = RoutePolylinesParser.parse(result)?.route
        console.log("[DEBUG] ", route)
        if (route) {
          return route.routes[0].legs.map(({ polyline }) => polyline)
        }
      }
    } catch (e) {
      return []
    }
    return []
  }

  private removePassthroughWaypoint = async (
    event: MapLibreGlDirectionsWaypointEvent,
  ) => {
    if (!this.route) {
      return
    }
    try {
      this.isUpdatingRoute = true
      const stops = this.enrichStops(this.route.stops)
      if (event.data?.index !== undefined && stops) {
        const shouldChangeIndex = !(
          this.isAnalyzeOrderMap && !this.currentLocation
        )

        const changeIndexBy = shouldChangeIndex
          ? -1 +
            (stops || []).filter((s) => s.proximity_status === "left").length
          : 0

        const indexToRemove = event.data.index + changeIndexBy
        const updated = [
          ...stops.slice(0, indexToRemove - 1),
          ...stops.slice(indexToRemove),
        ]
        this.onRouteStopsUpdate?.(updated)
      }
    } catch (e) {
      this.fetchRoute(true)
    } finally {
      this.isUpdatingRoute = false
    }
  }

  private moveWaypoint = async (event: MapLibreGlDirectionsWaypointEvent) => {
    if (
      event.originalEvent?.type === "mouseup" &&
      // @ts-ignore
      this.directions?.waypointBeingDragged?.properties?.action !== undefined
    ) {
      try {
        this.isUpdatingRoute = true
        const stops = this.enrichStops(this.route?.stops ?? null)
        const indexToUpdate = event.data?.index

        const shouldChangeIndex = !(
          this.isAnalyzeOrderMap && !this.currentLocation
        )
        // MapLibreEvent index is always moved by 1, because we are showing start of route as a first waypoint,
        // We also need to move 'forward' because polylines are not returning left stops
        // However if we are displaying analyze order map with current location we should not change index

        const changeIndexBy = shouldChangeIndex
          ? -1 +
            (stops || []).filter((s) => s.proximity_status === "left").length
          : 0
        if (indexToUpdate !== undefined && this.directions && stops) {
          let location: DetailedLocation | undefined
          try {
            location = await this.reverseGeocode(
              this.directions.waypoints[indexToUpdate][1],
              this.directions.waypoints[indexToUpdate][0],
            )
          } catch {
            // Geocoding failed, will use coordinates as fallback
          }
          const formattedAddress = `${this.directions.waypoints[indexToUpdate][1]},${this.directions.waypoints[indexToUpdate][0]}`
          const currentStop = stops[indexToUpdate + changeIndexBy]
          const updatedStop = {
            ...currentStop,
            action:
              currentStop.action === "from_driver_location"
                ? "other"
                : currentStop.action,
            by: undefined,
            location: location ?? { formattedAddress },
            msgId: undefined,
            addressUpdated: true,
          }
          const updated = [
            ...stops.slice(0, indexToUpdate + changeIndexBy),
            updatedStop,
            ...stops.slice(indexToUpdate + changeIndexBy + 1),
          ]
          try {
            await this.onRouteStopsUpdate?.(updated)
            // Re-establish hover handlers after route update
            setTimeout(() => {
              this.addHoverHandlers()
            }, 100)
          } catch (e) {
            this.fetchRoute(true)
          }
        }
      } finally {
        this.isUpdatingRoute = false
      }
    }
  }

  public enrichStops(stops: RouteStop[] | null) {
    if (stops) {
      return stops.map((stop) => {
        const currentTarget = findCurrentTarget(stops)
        return {
          ...stop,
          is_active: isStopActive(stop),
          is_current_target: currentTarget === stop,
          is_late: isStopLate(stop),
          isPending: isStopPending(stop),
          shouldAlert: false,
        }
      })
    }
    return stops
  }

  private async reverseGeocode(
    lat: number | string,
    lng: number | string,
    companyName?: string,
  ): Promise<DetailedLocation> {
    const coordinates: google.maps.LatLngLiteral = {
      lat: typeof lat === "string" ? parseFloat(lat) : lat,
      lng: typeof lng === "string" ? parseFloat(lng) : lng,
    }

    const geocoder = new google.maps.Geocoder()

    return new Promise((resolve, reject) => {
      void geocoder.geocode({ location: coordinates }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const result = results[0]
          let country = ""
          let street = ""
          let locality = ""
          let postalCode = ""
          let countryCode = ""

          result.address_components.forEach((component) => {
            if (component.types.includes("country")) {
              country = component.long_name
              countryCode = component.short_name
            }
            if (component.types.includes("locality")) {
              locality = component.long_name
            }
            if (component.types.includes("route")) {
              street = component.long_name
            }
            if (component.types.includes("street_number")) {
              street = `${component.long_name} ${street}`.trim()
            }
            if (component.types.includes("postal_code")) {
              postalCode = component.long_name
            }
          })

          resolve({
            lat: `${coordinates.lat}`,
            lon: `${coordinates.lng}`,
            country,
            street,
            locality,
            postalCode,
            countryCode,
            formattedAddress: result.formatted_address,
            companyName,
          })
        } else {
          reject(new Error(`Geocoding failed: ${status}`))
        }
      })
    })
  }

  public showEntireRoute() {
    if (!this.mapInstance) return
    const polylines = (this.routeInfo?.routes[0]?.legs ?? []).map((leg) =>
      polyline.decode(leg.polyline),
    )

    const bounds = new LngLatBounds()
    polylines
      .flatMap((polyline) => (Array.isArray(polyline) ? polyline : []))
      .forEach(([lat, lng]) => bounds.extend([lng, lat] as [number, number]))
    // The stops already done and the driven trail belong to the route too,
    // and stay on the map after the last active stop is gone.
    const overlayPoints = pastStopsOverlayPoints(this.pastStopsOverlay)
    overlayPoints.forEach((point) => bounds.extend(point))
    if (overlayPoints.length > 0 && this.currentLocation) {
      bounds.extend(this.currentLocation)
    }
    if (bounds.isEmpty()) return

    // Use larger padding for analyze order map to avoid covering routes
    // with the alternative routes selector table (positioned at bottom-left).
    // On a phone that table is a one-line pill and the whole canvas is
    // ~390×300px — the desktop padding would exceed the canvas and MapLibre
    // then rejects the fit altogether, leaving the map zoomed in on the first
    // stop. Scale the padding to the canvas instead.
    const container = this.mapInstance.getContainer()
    const canvasWidth = container?.clientWidth ?? 0
    const canvasHeight = container?.clientHeight ?? 0
    const isNarrowCanvas = canvasWidth > 0 && canvasWidth < 768
    const padding = this.isAnalyzeOrderMap
      ? isNarrowCanvas
        ? {
            top: 40,
            right: 40,
            bottom: Math.min(80, Math.floor(canvasHeight / 4)),
            left: 40,
          }
        : {
            top: 40,
            right: 40,
            bottom: Math.min(350, Math.floor(canvasHeight / 2)),
            left: Math.min(550, Math.floor(canvasWidth / 2)),
          }
      : 40

    this.mapInstance.fitBounds(bounds, {
      padding,
      maxZoom: 15,
      duration: 1000,
    })
  }

  public initializeMap() {
    this.handleAmericanaStyle()
    this.addMapControls()
    this.mapInstance.on("load", this.handleMapLoaded)
    this.detachRegionBorders?.()
    this.detachRegionBorders = attachRegionBorders(this.mapInstance)
  }

  public initializeDistancePopup() {
    if (
      this.mapInstance &&
      this.directions &&
      this.route?.stops &&
      this.route?.stops.length > 0 &&
      this.polylines
    ) {
      const { totalDuration, totalDistance } = sumRouteMetrics(this.route)
      const popupPosition = this.directions.findVisibleRouteCenter(
        this.polylines,
      )

      this.createRouteInfoPopup(popupPosition, {
        totalDuration,
        totalDistance,
        polylines: this.polylines,
      })
      this.mapInstance.on("moveend", this.updateDistancePopupPosition)
      this.mapInstance.on("zoomend", this.updateDistancePopupPosition)
    } else {
      if (this.distancePopup) {
        this.distancePopup.off("close", this.handleDistancePopupClose)
        this.distancePopup.remove()
      }
    }
  }

  public isDestroyed(): boolean {
    return this.destroyed
  }

  public clearMap() {
    // Idempotent: MapView tears down on unmount and changeMapStyle tears down
    // before rebuilding, so this can be reached twice for the same instance.
    if (this.destroyed) return
    this.destroyed = true
    this.detachRegionBorders?.()
    this.detachRegionBorders = null
    // this.clearDistancePopup()
    // Remove event listeners from the map instance
    // Remove all sources and layers
    if (this.mapInstance.getLayer("location-point")) {
      this.mapInstance.removeLayer("location-point")
    }
    if (this.mapInstance.getSource("location-point")) {
      this.mapInstance.removeSource("location-point")
    }

    this.mapContainer?.removeEventListener(
      "routelinedragend",
      this.addNewPassthroughWaypoint,
    )

    // // Properly destroy the directions component
    if (this.directions) {
      // Remove specific event listeners attached to directions
      this.directions.off("removewaypoint", this.removePassthroughWaypoint)
      this.directions.off("movewaypoint", this.moveWaypoint)
      this.mapInstance?.off(
        "mousemove",
        "maplibre-gl-directions-waypoint-icon",
        this.handleIconMouseEnter,
      )
      this.mapInstance?.off(
        "mouseleave",
        "maplibre-gl-directions-waypoint-icon",
        this.handleIconMouseLeave,
      )
      this.directions.cancelFetch()
      this.directions.destroy()
      this.directions = null
    }

    if (this.avoidanceAreasManager) {
      this.avoidanceAreasManager.destroy()
    }

    if (this.distancePopup) {
      this.distancePopup.off("close", this.handleDistancePopupClose)
      this.distancePopup.remove()
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove()
      this.hoverPopup = null
    }
    this.clearFuelStationsInteractivity()
    this.clearAutohofsInteractivity()
    this.clearPastStopsInteractivity()
    this.mapInstance?.off("moveend", this.updateDistancePopupPosition)
    this.mapInstance?.off("zoomend", this.updateDistancePopupPosition)

    // Remove the map instance itself
    this.mapInstance.remove()
    this.polylines = null
  }

  public setIsReadOnlyMap(isReadOnly: boolean) {
    this.isReadOnlyMap = isReadOnly
  }

  public setFuelStations(stations: FuelStation[]) {
    this.fuelStations = stations
    this.updateFuelStationsLayer()
  }

  public setFuelStationsVisible(isVisible: boolean) {
    this.fuelStationsVisible = isVisible
    this.updateFuelStationsVisibility()
  }

  public setFuelStationsVisibleCategories(categories: string[]) {
    this.fuelStationsVisibleCategories = new Set(categories)
    this.updateFuelStationsLayer()
  }

  public setAutohofs(autohofs: Autohof[]) {
    this.autohofs = autohofs
    this.updateAutohofsLayer()
  }

  /**
   * HERE traffic on the stored route. Turning colors on or off redraws the
   * line from the polylines already here; nothing is fetched from gps.
   */
  public setRouteTraffic(settings: RouteTrafficSettings) {
    const colorsChanged = settings.colors !== this.routeTraffic.colors
    this.routeTraffic = settings
    if (colorsChanged && this.directions && this.polylines && this.route) {
      void this.directions
        .setInitialRoute(
          this.polylines,
          this.sections ?? [],
          this.route,
          this.legTraffic(this.routeInfo),
        )
        .then(() => this.updateRouteLayerColors())
    }
    this.updateTrafficIncidents()
    if (colorsChanged) this.redrawRouteInfoPopup?.()
  }

  public setShowStopNumbers(visible: boolean) {
    this.showStopNumbers = visible
    if (this.mapInstance?.getLayer(STOP_NUMBER_LAYER_ID)) {
      this.mapInstance.setLayoutProperty(
        STOP_NUMBER_LAYER_ID,
        "visibility",
        visible ? "visible" : "none",
      )
    }
  }

  /** From the incident list: move to the incident and open its box. */
  public showTrafficIncident(incident: TrafficIncident) {
    if (this.trafficIncidentsLayer?.map !== this.mapInstance) {
      this.trafficIncidentsLayer = null
    }
    this.trafficIncidentsLayer ??= new TrafficIncidentsLayer(this.mapInstance)
    this.trafficIncidentsLayer.showIncident(incident)
  }

  private legTraffic(
    routeInfo: RouteInfo | null,
  ): (TrafficRange[] | undefined)[] | undefined {
    return this.routeTraffic.colors
      ? routeInfo?.routes?.[0]?.legs?.map((leg) => leg.traffic)
      : undefined
  }

  private updateTrafficIncidents() {
    // A style change makes a new map; the old layer went with the old one
    if (this.trafficIncidentsLayer?.map !== this.mapInstance) {
      this.trafficIncidentsLayer = null
    }
    const incidents = filterIncidentsByType(
      routeIncidents(this.routeInfo?.routes?.[0]?.legs, new Date()),
      this.routeTraffic.incidentTypes,
    )
    if (!incidents.length) {
      this.trafficIncidentsLayer?.remove()
      this.trafficIncidentsLayer = null
      return
    }
    this.trafficIncidentsLayer ??= new TrafficIncidentsLayer(this.mapInstance)
    this.trafficIncidentsLayer.setIncidents(incidents)
  }

  public setAutohofsVisible(isVisible: boolean) {
    this.autohofsVisible = isVisible
    this.updateAutohofsVisibility()
  }

  /** `null` takes the past stops and the driven trail off the map. */
  public setPastStopsOverlay(overlay: PastStopsOverlay | null) {
    this.pastStopsOverlay = overlay
    this.updatePastStopsLayers()
  }

  private addAttributionControl() {
    let attribution: string | null = null

    if (this.routingProvider === "here") {
      attribution = "Here.com"
    } else if (this.routingProvider === "tomtom") {
      attribution = "TomTom"
    } else if (this.routingProvider === "ptv") {
      attribution = "PTV Group"
    }
    if (attribution) {
      this.mapInstance.addControl(
        new AttributionControl({
          compact: true,
          customAttribution: i18n.t("maps.mapManager.directionsProvidedBy", {
            provider: attribution,
          }),
        }),
      )
      // MapLibre opens the compact attribution until the first drag. On a
      // phone-sized canvas that two-line strip covers the bottom of the map
      // (and sits on top of the route pill), so start it folded to the (i)
      // button there — tapping it still shows the full attribution.
      if (this.mapInstance.getContainer().clientWidth < 768) {
        this.mapInstance
          .getContainer()
          .querySelector(".maplibregl-ctrl-attrib")
          ?.classList.remove("maplibregl-compact-show")
      }
    }
  }

  private addNavigationControl() {
    this.mapInstance.addControl(
      new NavigationControl({
        visualizePitch: true,
        visualizeRoll: true,
        showZoom: true,
        showCompass: true,
      }),
      isMobile ? "bottom-left" : "top-right",
    )
  }

  // Graphical scale bar. Kept metric everywhere - the fleet drives in km.
  private addScaleControl() {
    this.mapInstance.addControl(
      new ScaleControl({ unit: "metric" }),
      "bottom-right",
    )
  }

  private addMapControls() {
    this.addAttributionControl()
    this.addNavigationControl()
    this.addScaleControl()
  }

  public getMapInstance(): MapLibreMap {
    return this.mapInstance
  }

  public getMapContainer(): HTMLDivElement {
    return this.mapContainer
  }

  public getIsDarkMode(): boolean {
    return this.isDarkMode
  }

  public getRouteJid(): string | null {
    return this.routeJid
  }

  public getIsAnalyzeOrderMap(): boolean {
    return this.isAnalyzeOrderMap
  }

  public setIsDarkMode(isDarkMode: boolean) {
    this.isDarkMode = isDarkMode
    this.changeMapStyle(this.mapConfig.style)
  }

  public changeMapStyle = async (style: MapStyle) => {
    const currentCenter = [
      this.mapInstance.getCenter().lng,
      this.mapInstance.getCenter().lat,
    ] as [number, number]
    const zoom = this.mapInstance.getZoom()

    this.mapConfig.style = style
    this.mapConfig.center = currentCenter
    this.mapConfig.zoom = zoom
    this.clearMap()
    // A fresh instance follows, so the manager is usable again.
    this.destroyed = false
    this.mapInstance = new MapLibreMap({
      container: this.mapContainer,
      style: this.getMapStyleUrl(style, this.isDarkMode),
      center: this.mapConfig.center,
      zoom: this.mapConfig.zoom,
      attributionControl: false, // We have custom attribution with map provider information
    })
    this.initializeMap()
  }

  private handleAmericanaStyle() {
    this.shieldRenderer = new URLShieldRenderer("/shields.json", routeParser)
      .filterImageID(shieldPredicate)
      .filterNetwork(networkPredicate)
      .renderOnMaplibreGL(this.mapInstance)
    this.mapInstance.on("styleimagemissing", this.onStyleImageMissing)
  }

  private onStyleImageMissing = (e: MapStyleImageMissingEvent) => {
    if (addMissingStopNumberBadge(this.mapInstance, e.id)) return
    switch (e.id.split("\n")[0]) {
      case "shield":
        break
      case "poi":
        Poi.missingIconHandler(this.shieldRenderer, this.mapInstance, e)
        break
      default:
        console.warn("Image id not recognized:", JSON.stringify(e.id))
        break
    }
  }

  private getMapStyleUrl(style: MapStyle, isDarkMode: boolean) {
    const styleConfig = MAP_STYLES[style]
    const baseUrl = isDarkMode ? styleConfig.darkUrl : styleConfig.url

    switch (styleConfig.tilesProvider) {
      case "osm":
        return baseUrl // No API key needed for OSM
      case "maptiler":
        return `${baseUrl}?key=${MAPTILER_KEY}`
      default:
        return baseUrl
    }
  }

  private removeFuelStationsLayer() {
    this.clearFuelStationsInteractivity()

    FUEL_STATIONS_LAYER_IDS.forEach((layerId) => {
      if (this.mapInstance.getLayer(layerId)) {
        this.mapInstance.removeLayer(layerId)
      }
    })

    if (this.mapInstance.getSource(FUEL_STATIONS_SOURCE_ID)) {
      this.mapInstance.removeSource(FUEL_STATIONS_SOURCE_ID)
    }
  }

  private updateFuelStationsVisibility() {
    if (!this.mapInstance.getLayer(FUEL_STATIONS_LAYER_ID)) {
      return
    }

    if (!this.fuelStationsVisible && this.fuelStationsPopup) {
      this.fuelStationsPopup.remove()
      this.fuelStationsPopup = null
    }

    if (!this.fuelStationsVisible) {
      this.setFuelStationsPointerCursor(false)
    }

    FUEL_STATIONS_LAYER_IDS.forEach((layerId) => {
      if (this.mapInstance.getLayer(layerId)) {
        this.mapInstance.setLayoutProperty(
          layerId,
          "visibility",
          this.fuelStationsVisible ? "visible" : "none",
        )
      }
    })
  }

  private ensureFuelStationIcons(): Promise<void> {
    // changeMapStyle creates a brand-new map instance without the previously
    // added images, so the cache is only valid for the instance it was
    // created for.
    if (
      !this.fuelStationIconsPromise ||
      this.fuelStationIconsMapInstance !== this.mapInstance
    ) {
      const map = this.mapInstance
      this.fuelStationIconsMapInstance = map
      this.fuelStationIconsPromise = Promise.all(
        Object.entries(FUEL_STATION_ICONS).map(async ([icon, url]) => {
          const imageId = fuelStationIconImageId(icon)
          if (map.hasImage(imageId)) {
            return
          }

          try {
            const image = await map.loadImage(url)
            if (!map.hasImage(imageId)) {
              // pixelRatio normalizes differently sized source images to a
              // common on-screen size.
              map.addImage(imageId, image.data, {
                pixelRatio: image.data.width / FUEL_STATION_ICON_SIZE_PX,
              })
            }
          } catch (error) {
            console.error(`Failed to load fuel station icon "${icon}":`, error)
          }
        }),
      ).then(() => undefined)
    }

    return this.fuelStationIconsPromise
  }

  private getAboveRouteBeforeId(): string | undefined {
    return getAboveRouteBeforeId(this.mapInstance)
  }

  private raiseLayersAboveRouteLines() {
    if (!this.mapInstance) {
      return
    }

    raiseLayersAboveRouteLines(this.mapInstance, ABOVE_ROUTE_LAYER_IDS)
  }

  private updateFuelStationsLayer() {
    if (!this.mapInstance) {
      return
    }

    // Don't silently drop the update while the style is still loading —
    // retry once the map settles, otherwise the layer keeps showing data
    // from before the change (e.g. a stale category selection).
    if (!this.mapInstance.isStyleLoaded()) {
      if (!this.fuelStationsUpdateQueued) {
        this.fuelStationsUpdateQueued = true
        this.mapInstance.once("idle", () => {
          this.fuelStationsUpdateQueued = false
          this.updateFuelStationsLayer()
        })
      }
      return
    }

    if (this.fuelStations.length === 0) {
      this.removeFuelStationsLayer()
      return
    }

    const hasIcons = this.fuelStations.some(
      (station) => station.icon && FUEL_STATION_ICONS[station.icon],
    )

    if (!hasIcons) {
      this.renderFuelStationsLayers()
      return
    }

    void this.ensureFuelStationIcons().then(() => {
      if (this.fuelStations.length > 0) {
        this.renderFuelStationsLayers()
      }
    })
  }

  private renderFuelStationsLayers() {
    if (!this.mapInstance) {
      return
    }

    if (!this.mapInstance.isStyleLoaded()) {
      this.updateFuelStationsLayer()
      return
    }

    // Categorized stations are opt-in: only explicitly enabled categories
    // show up. Stations without a category (e.g. chomar.pl) are always shown.
    const visibleStations = this.fuelStations.filter(
      (station) =>
        !station.category ||
        this.fuelStationsVisibleCategories.has(station.category),
    )

    const data = {
      type: "FeatureCollection" as const,
      features: visibleStations.map((station) => ({
        type: "Feature" as const,
        properties: {
          color: normalizeFuelStationColor(station.color),
          ...(station.icon && FUEL_STATION_ICONS[station.icon]
            ? { icon: station.icon }
            : {}),
          name: station.name,
          // Stations without a street address show their category (map
          // layer name) in the popup instead.
          address: station.address || station.category || "",
        },
        geometry: {
          type: "Point" as const,
          coordinates: [station.lon, station.lat],
        },
      })),
    }

    const source = this.mapInstance.getSource(FUEL_STATIONS_SOURCE_ID) as
      | GeoJSONSource
      | undefined

    if (source) {
      source.setData(data)
      this.raiseLayersAboveRouteLines()
      this.updateFuelStationsVisibility()
      return
    }

    this.mapInstance.addSource(FUEL_STATIONS_SOURCE_ID, {
      type: "geojson",
      data,
    })

    const beforeId = this.getAboveRouteBeforeId()

    this.mapInstance.addLayer(
      {
        id: FUEL_STATIONS_LAYER_ID,
        type: "circle",
        source: FUEL_STATIONS_SOURCE_ID,
        filter: ["!", ["has", "icon"]],
        paint: {
          "circle-color": ["get", "color"],
          // Grows with zoom so the dots stay readable next to the route line
          // and on phone screens, where they used to be barely visible.
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            5,
            12,
            7.5,
            16,
            9,
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            1.5,
            16,
            2.5,
          ],
          "circle-opacity": 1,
        },
      },
      beforeId,
    )

    this.mapInstance.addLayer(
      {
        id: FUEL_STATIONS_ICON_LAYER_ID,
        type: "symbol",
        source: FUEL_STATIONS_SOURCE_ID,
        filter: ["has", "icon"],
        layout: {
          "icon-image": ["concat", "fuel-station-", ["get", "icon"]],
          "icon-size": ["interpolate", ["linear"], ["zoom"], 6, 1, 16, 1.2],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      },
      beforeId,
    )

    this.bindFuelStationsInteractivity()
    this.raiseLayersAboveRouteLines()
    this.updateFuelStationsVisibility()
  }

  private ensureAutohofIcon(): Promise<void> {
    if (
      !this.autohofIconPromise ||
      this.autohofIconMapInstance !== this.mapInstance
    ) {
      const map = this.mapInstance
      this.autohofIconMapInstance = map
      this.autohofIconPromise = (async () => {
        if (map.hasImage(AUTOHOF_ICON_IMAGE_ID)) {
          return
        }

        try {
          const image = await map.loadImage(AUTOHOF_ICON_URL)
          if (!map.hasImage(AUTOHOF_ICON_IMAGE_ID)) {
            map.addImage(AUTOHOF_ICON_IMAGE_ID, image.data, {
              pixelRatio: image.data.width / FUEL_STATION_ICON_SIZE_PX,
            })
          }
        } catch (error) {
          console.error("Failed to load autohof icon:", error)
        }
      })()
    }

    return this.autohofIconPromise
  }

  private removeAutohofsLayer() {
    this.clearAutohofsInteractivity()

    if (this.mapInstance.getLayer(AUTOHOFS_LAYER_ID)) {
      this.mapInstance.removeLayer(AUTOHOFS_LAYER_ID)
    }

    if (this.mapInstance.getSource(AUTOHOFS_SOURCE_ID)) {
      this.mapInstance.removeSource(AUTOHOFS_SOURCE_ID)
    }
  }

  private updateAutohofsVisibility() {
    if (!this.mapInstance.getLayer(AUTOHOFS_LAYER_ID)) {
      return
    }

    if (!this.autohofsVisible && this.fuelStationsPopup) {
      this.fuelStationsPopup.remove()
      this.fuelStationsPopup = null
    }

    this.mapInstance.setLayoutProperty(
      AUTOHOFS_LAYER_ID,
      "visibility",
      this.autohofsVisible ? "visible" : "none",
    )
  }

  private updateAutohofsLayer() {
    if (!this.mapInstance) {
      return
    }

    if (!this.mapInstance.isStyleLoaded()) {
      if (!this.autohofsUpdateQueued) {
        this.autohofsUpdateQueued = true
        this.mapInstance.once("idle", () => {
          this.autohofsUpdateQueued = false
          this.updateAutohofsLayer()
        })
      }
      return
    }

    if (this.autohofs.length === 0) {
      this.removeAutohofsLayer()
      return
    }

    void this.ensureAutohofIcon().then(() => {
      if (this.autohofs.length > 0) {
        this.renderAutohofsLayer()
      }
    })
  }

  private renderAutohofsLayer() {
    if (!this.mapInstance) {
      return
    }

    if (!this.mapInstance.isStyleLoaded()) {
      this.updateAutohofsLayer()
      return
    }

    const data = {
      type: "FeatureCollection" as const,
      features: this.autohofs.map((autohof) => ({
        type: "Feature" as const,
        properties: {
          name: autohof.name,
          address: autohof.address,
        },
        geometry: {
          type: "Point" as const,
          coordinates: [autohof.lon, autohof.lat],
        },
      })),
    }

    const source = this.mapInstance.getSource(AUTOHOFS_SOURCE_ID) as
      | GeoJSONSource
      | undefined

    if (source) {
      source.setData(data)
      this.raiseLayersAboveRouteLines()
      this.updateAutohofsVisibility()
      return
    }

    this.mapInstance.addSource(AUTOHOFS_SOURCE_ID, {
      type: "geojson",
      data,
    })

    this.mapInstance.addLayer(
      {
        id: AUTOHOFS_LAYER_ID,
        type: "symbol",
        source: AUTOHOFS_SOURCE_ID,
        layout: {
          "icon-image": AUTOHOF_ICON_IMAGE_ID,
          "icon-size": ["interpolate", ["linear"], ["zoom"], 6, 1, 16, 1.2],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      },
      this.getAboveRouteBeforeId(),
    )

    this.bindAutohofsInteractivity()
    this.raiseLayersAboveRouteLines()
    this.updateAutohofsVisibility()
  }

  private clearAutohofsInteractivity() {
    this.autohofsEventHandlers.forEach(({ event, handler }) => {
      this.mapInstance?.off(event, AUTOHOFS_LAYER_ID, handler)
    })
    this.autohofsEventHandlers = []
    this.autohofsUpdateQueued = false
  }

  private bindAutohofsInteractivity() {
    if (
      !this.mapInstance ||
      !this.mapInstance.getLayer(AUTOHOFS_LAYER_ID) ||
      this.autohofsEventHandlers.length > 0
    ) {
      return
    }

    const clickHandler = (
      e: MapMouseEvent & { features?: MapGeoJSONFeature[] | undefined },
    ) => {
      if (!e.features?.length) {
        return
      }

      const properties = e.features[0].properties as
        | { name?: string; address?: string }
        | undefined
      const geometry = e.features[0].geometry as
        | { coordinates?: [number, number] }
        | undefined

      if (!properties) {
        return
      }

      const [lon, lat] = geometry?.coordinates ?? [e.lngLat.lng, e.lngLat.lat]

      this.fuelStationsPopup?.remove()
      this.fuelStationsPopup = new Popup({
        closeButton: true,
        closeOnClick: true,
        focusAfterOpen: false,
      })
        .setLngLat(e.lngLat)
        .setDOMContent(
          this.createFuelStationPopupContent(
            properties.name ?? "",
            properties.address ?? "",
            lat,
            lon,
          ),
        )
        .addTo(this.mapInstance)
    }

    const mouseEnterHandler = () => this.setFuelStationsPointerCursor(true)
    const mouseMoveHandler = () => this.setFuelStationsPointerCursor(true)
    const mouseLeaveHandler = () => this.setFuelStationsPointerCursor(false)

    this.mapInstance.on("click", AUTOHOFS_LAYER_ID, clickHandler)
    this.mapInstance.on("mouseenter", AUTOHOFS_LAYER_ID, mouseEnterHandler)
    this.mapInstance.on("mousemove", AUTOHOFS_LAYER_ID, mouseMoveHandler)
    this.mapInstance.on("mouseleave", AUTOHOFS_LAYER_ID, mouseLeaveHandler)

    this.autohofsEventHandlers = [
      { event: "click", handler: clickHandler },
      { event: "mouseenter", handler: mouseEnterHandler },
      { event: "mousemove", handler: mouseMoveHandler },
      { event: "mouseleave", handler: mouseLeaveHandler },
    ]
  }

  private updatePastStopsLayers() {
    if (!this.mapInstance || this.destroyed) {
      return
    }

    if (!this.mapInstance.isStyleLoaded()) {
      if (!this.pastStopsUpdateQueued) {
        this.pastStopsUpdateQueued = true
        this.mapInstance.once("idle", () => {
          this.pastStopsUpdateQueued = false
          this.updatePastStopsLayers()
        })
      }
      return
    }

    if (!this.pastStopsOverlay) {
      this.removePastStopsLayers()
      return
    }

    this.renderPastStopsLayers(this.pastStopsOverlay)
  }

  private renderPastStopsLayers(overlay: PastStopsOverlay) {
    const map = this.mapInstance
    const trailData = pastTrailFeatureCollection(overlay.trail)
    const stopsData = pastStopsFeatureCollection(overlay.stops)

    const trailSource = map.getSource(PAST_TRAIL_SOURCE_ID) as
      | GeoJSONSource
      | undefined
    if (trailSource) {
      trailSource.setData(trailData)
    } else {
      map.addSource(PAST_TRAIL_SOURCE_ID, { type: "geojson", data: trailData })
    }

    const trailBeforeId = getBelowRouteLinesBeforeId(
      map
        .getLayersOrder()
        .filter(
          (id) =>
            id !== PAST_TRAIL_LAYER_ID && id !== PAST_TRAIL_CASING_LAYER_ID,
        ),
    )
    if (map.getLayer(PAST_TRAIL_CASING_LAYER_ID)) {
      map.moveLayer(PAST_TRAIL_CASING_LAYER_ID, trailBeforeId)
    } else {
      map.addLayer(
        {
          id: PAST_TRAIL_CASING_LAYER_ID,
          type: "line",
          source: PAST_TRAIL_SOURCE_ID,
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": "#ffffff",
            "line-width": 7,
            "line-opacity": 0.75,
          },
        },
        trailBeforeId,
      )
    }
    if (map.getLayer(PAST_TRAIL_LAYER_ID)) {
      map.setPaintProperty(
        PAST_TRAIL_LAYER_ID,
        "line-color",
        pastTrailColor(this.isDarkMode),
      )
      map.moveLayer(PAST_TRAIL_LAYER_ID, trailBeforeId)
    } else {
      map.addLayer(
        {
          id: PAST_TRAIL_LAYER_ID,
          type: "line",
          source: PAST_TRAIL_SOURCE_ID,
          layout: {
            "line-cap": "butt",
            "line-join": "round",
          },
          paint: {
            "line-color": pastTrailColor(this.isDarkMode),
            "line-width": 4,
            "line-opacity": 0.85,
            "line-dasharray": [2, 1.5],
          },
        },
        trailBeforeId,
      )
    }

    this.ensurePastStopIcons()
    const stopsSource = map.getSource(PAST_STOPS_SOURCE_ID) as
      | GeoJSONSource
      | undefined
    if (stopsSource) {
      stopsSource.setData(stopsData)
    } else {
      map.addSource(PAST_STOPS_SOURCE_ID, { type: "geojson", data: stopsData })
    }

    if (!map.getLayer(PAST_STOPS_LAYER_ID)) {
      map.addLayer(
        {
          id: PAST_STOPS_LAYER_ID,
          type: "symbol",
          source: PAST_STOPS_SOURCE_ID,
          layout: {
            "icon-image": [
              "match",
              ["get", "status"],
              "skipped",
              PAST_STOP_SKIPPED_IMAGE_ID,
              PAST_STOP_LEFT_IMAGE_ID,
            ],
            "icon-size": ["interpolate", ["linear"], ["zoom"], 6, 0.8, 16, 1.1],
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          },
        },
        this.getAboveRouteBeforeId(),
      )
      this.bindPastStopsInteractivity()
    }

    this.raiseLayersAboveRouteLines()
  }

  private ensurePastStopIcons() {
    const icons = [
      { id: PAST_STOP_LEFT_IMAGE_ID, svg: PAST_STOP_LEFT_SVG },
      { id: PAST_STOP_SKIPPED_IMAGE_ID, svg: PAST_STOP_SKIPPED_SVG },
    ]
    for (const icon of icons) {
      if (!this.mapInstance.hasImage(icon.id)) {
        this.mapInstance.addImage(
          icon.id,
          svgToCanvas(this.mapInstance, icon.svg),
          { pixelRatio: 2 },
        )
      }
    }
  }

  private removePastStopsLayers() {
    this.clearPastStopsInteractivity()

    for (const layerId of [
      PAST_STOPS_LAYER_ID,
      PAST_TRAIL_LAYER_ID,
      PAST_TRAIL_CASING_LAYER_ID,
    ]) {
      if (this.mapInstance.getLayer(layerId)) {
        this.mapInstance.removeLayer(layerId)
      }
    }
    for (const sourceId of [PAST_STOPS_SOURCE_ID, PAST_TRAIL_SOURCE_ID]) {
      if (this.mapInstance.getSource(sourceId)) {
        this.mapInstance.removeSource(sourceId)
      }
    }
  }

  private clearPastStopsInteractivity() {
    this.pastStopsEventHandlers.forEach(({ event, handler }) => {
      this.mapInstance?.off(event, PAST_STOPS_LAYER_ID, handler)
    })
    this.pastStopsEventHandlers = []
    // A retry queued on the old map instance never fires after clearMap;
    // allow the next update to queue again.
    this.pastStopsUpdateQueued = false
    this.closePastStopPopup()
    this.setFuelStationsPointerCursor(false)
  }

  private closePastStopPopup() {
    this.pastStopsPopup?.remove()
    this.pastStopsPopup = null
    this.pastStopsPopupPinned = false
  }

  private showPastStopPopup(e: PastStopMouseEvent, pinned: boolean) {
    const feature = e.features?.[0]
    if (!feature?.properties) {
      return
    }
    // Hovering another marker must not replace a popup the user clicked open.
    if (!pinned && this.pastStopsPopupPinned) {
      return
    }

    const geometry = feature.geometry as
      | { coordinates?: [number, number] }
      | undefined
    const lngLat = geometry?.coordinates ?? [e.lngLat.lng, e.lngLat.lat]

    this.closePastStopPopup()
    this.pastStopsPopupPinned = pinned
    const popup = new Popup({
      closeButton: pinned,
      closeOnClick: pinned,
      focusAfterOpen: false,
      offset: 14,
    })
      .setLngLat(lngLat)
      .setDOMContent(
        this.createPastStopPopupContent(
          feature.properties as PastStopFeatureProps,
        ),
      )
      .addTo(this.mapInstance)
    popup.on("close", () => {
      if (this.pastStopsPopup === popup) {
        this.pastStopsPopup = null
        this.pastStopsPopupPinned = false
      }
    })
    this.pastStopsPopup = popup
  }

  private createPastStopPopupContent(props: PastStopFeatureProps) {
    const { title, details, status } = pastStopPopupContent(props)

    const container = document.createElement("div")
    container.className =
      "rounded-lg bg-white p-2 text-slate-900 shadow-lg dark:bg-gray-800 dark:text-white"

    const titleElement = document.createElement("div")
    titleElement.className = "text-xs font-semibold"
    titleElement.textContent = title
    container.appendChild(titleElement)

    details.forEach((detail) => {
      const element = document.createElement("div")
      element.className = "text-xs text-slate-600 dark:text-gray-300"
      element.textContent = detail
      container.appendChild(element)
    })

    const statusElement = document.createElement("div")
    statusElement.className =
      props.status === "skipped"
        ? "mt-1 text-xs font-medium text-amber-600 dark:text-amber-400"
        : "mt-1 text-xs font-medium text-green-600 dark:text-green-400"
    statusElement.textContent = status
    container.appendChild(statusElement)

    return container
  }

  private bindPastStopsInteractivity() {
    if (
      !this.mapInstance ||
      !this.mapInstance.getLayer(PAST_STOPS_LAYER_ID) ||
      this.pastStopsEventHandlers.length > 0
    ) {
      return
    }

    const clickHandler = (e: PastStopMouseEvent) => {
      this.showPastStopPopup(e, true)
    }
    const mouseEnterHandler = (e: PastStopMouseEvent) => {
      this.setFuelStationsPointerCursor(true)
      this.showPastStopPopup(e, false)
    }
    const mouseMoveHandler = () => this.setFuelStationsPointerCursor(true)
    const mouseLeaveHandler = () => {
      this.setFuelStationsPointerCursor(false)
      if (!this.pastStopsPopupPinned) {
        this.closePastStopPopup()
      }
    }

    this.mapInstance.on("click", PAST_STOPS_LAYER_ID, clickHandler)
    this.mapInstance.on("mouseenter", PAST_STOPS_LAYER_ID, mouseEnterHandler)
    this.mapInstance.on("mousemove", PAST_STOPS_LAYER_ID, mouseMoveHandler)
    this.mapInstance.on("mouseleave", PAST_STOPS_LAYER_ID, mouseLeaveHandler)

    this.pastStopsEventHandlers = [
      { event: "click", handler: clickHandler },
      { event: "mouseenter", handler: mouseEnterHandler },
      { event: "mousemove", handler: mouseMoveHandler },
      { event: "mouseleave", handler: mouseLeaveHandler },
    ]
  }

  private clearFuelStationsInteractivity() {
    this.fuelStationsEventHandlers.forEach(({ event, layerId, handler }) => {
      this.mapInstance?.off(event, layerId, handler)
    })
    this.fuelStationsEventHandlers = []
    // A retry queued on the old map instance never fires after clearMap;
    // allow the next update to queue again.
    this.fuelStationsUpdateQueued = false

    if (this.fuelStationsPopup) {
      this.fuelStationsPopup.remove()
      this.fuelStationsPopup = null
    }

    this.setFuelStationsPointerCursor(false)
  }

  private setFuelStationsPointerCursor(enabled: boolean) {
    this.mapInstance
      ?.getCanvasContainer()
      .classList.toggle("maplibregl-track-pointer", enabled)
  }

  private bindFuelStationsInteractivity() {
    if (
      !this.mapInstance ||
      !this.mapInstance.getLayer(FUEL_STATIONS_LAYER_ID) ||
      this.fuelStationsEventHandlers.length > 0
    ) {
      return
    }

    const clickHandler = (
      e: MapMouseEvent & { features?: MapGeoJSONFeature[] | undefined },
    ) => {
      if (!e.features?.length) {
        return
      }

      const properties = e.features[0].properties as
        | {
            name?: string
            address?: string
          }
        | undefined
      const geometry = e.features[0].geometry as
        | { coordinates?: [number, number] }
        | undefined

      if (!properties) {
        return
      }

      const [lon, lat] = geometry?.coordinates ?? [e.lngLat.lng, e.lngLat.lat]

      this.fuelStationsPopup?.remove()
      this.fuelStationsPopup = new Popup({
        closeButton: true,
        closeOnClick: true,
        focusAfterOpen: false,
      })
        .setLngLat(e.lngLat)
        .setDOMContent(
          this.createFuelStationPopupContent(
            properties.name ?? "",
            properties.address ?? "",
            lat,
            lon,
          ),
        )
        .addTo(this.mapInstance)
    }

    const mouseEnterHandler = () => {
      this.setFuelStationsPointerCursor(true)
    }

    const mouseMoveHandler = () => {
      this.setFuelStationsPointerCursor(true)
    }

    const mouseLeaveHandler = () => {
      this.setFuelStationsPointerCursor(false)
    }

    FUEL_STATIONS_LAYER_IDS.forEach((layerId) => {
      if (!this.mapInstance.getLayer(layerId)) {
        return
      }

      this.mapInstance.on("click", layerId, clickHandler)
      this.mapInstance.on("mouseenter", layerId, mouseEnterHandler)
      this.mapInstance.on("mousemove", layerId, mouseMoveHandler)
      this.mapInstance.on("mouseleave", layerId, mouseLeaveHandler)

      this.fuelStationsEventHandlers.push(
        { event: "click", layerId, handler: clickHandler },
        { event: "mouseenter", layerId, handler: mouseEnterHandler },
        { event: "mousemove", layerId, handler: mouseMoveHandler },
        { event: "mouseleave", layerId, handler: mouseLeaveHandler },
      )
    })
  }

  private createFuelStationPopupContent(
    name: string,
    address: string,
    lat: number,
    lon: number,
  ) {
    const container = document.createElement("div")
    container.className =
      "rounded-lg bg-white p-3 text-slate-900 shadow-lg dark:bg-gray-800 dark:text-white"

    const title = document.createElement("div")
    title.className = "text-sm font-medium"
    title.textContent = name || i18n.t("maps.mapManager.fuelStation")
    container.appendChild(title)

    if (address) {
      const addressElement = document.createElement("div")
      addressElement.className =
        "mt-1 text-xs text-slate-600 dark:text-gray-300"
      addressElement.textContent = address
      container.appendChild(addressElement)
    }

    const actions = document.createElement("div")
    actions.className =
      "mt-3 flex flex-col gap-1 border-t border-slate-200 pt-2 dark:border-gray-700"

    actions.appendChild(
      this.createFuelStationActionButton(i18n.t("common.copy"), async () => {
        const coordinates = this.formatFuelStationCoordinates(lat, lon)
        const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`
        const textToCopy = [address || name, coordinates, googleMapsLink]
          .filter(Boolean)
          .join(" ")
        const toaster = window.mapToaster ?? window.appToaster

        try {
          await navigator.clipboard.writeText(textToCopy)
          toaster?.show({
            message: i18n.t("maps.mapManager.copiedToClipboard"),
            intent: "success",
            timeout: 2000,
          })
        } catch (error) {
          console.error("Failed to copy fuel station details:", error)
          toaster?.show({
            message: i18n.t("maps.mapManager.copyFailed"),
            intent: "danger",
            timeout: 2000,
          })
        }
      }),
    )

    if (this.canAddFuelStationStop()) {
      const addStopLabel = i18n.t("maps.mapManager.addStop")
      actions.appendChild(
        this.createFuelStationActionButton(addStopLabel, async () => {
          this.fuelStationsPopup?.remove()
          this.fuelStationsPopup = null

          const locationInfo: Pick<
            Location,
            "formattedAddress" | "companyName"
          > = {
            formattedAddress: `${lat},${lon}`,
            companyName: name || undefined,
          }

          await this.addStopAsDestination([lon, lat], "refuel", locationInfo)
        }),
      )
    }

    container.appendChild(actions)

    return container
  }

  private createFuelStationActionButton(
    label: string,
    onClick: () => Promise<void> | void,
  ) {
    const button = document.createElement("button")
    button.type = "button"
    button.className =
      "flex w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-700 transition hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-gray-700"
    button.textContent = label
    button.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      void onClick()
    })
    return button
  }

  private formatFuelStationCoordinates(lat: number, lon: number) {
    return `${lat.toFixed(5)}, ${lon.toFixed(5)}`
  }

  private canAddFuelStationStop() {
    return (
      !this.isSharedMap &&
      !this.isReadOnlyMap &&
      !!this.onRouteStopsUpdate &&
      (!!this.route?.stops || this.isAnalyzeOrderMap)
    )
  }

  public setTemporaryAvoidOptions(avoidOptions?: AvoidType[]) {
    this.temporaryAvoidOptions = avoidOptions || []
  }

  public setTemporaryLoadType(loadType?: LoadType[]) {
    this.temporaryLoadType = loadType || []
  }

  public setTemporaryCarConfig(carConfig?: CarRouteConfig) {
    this.temporaryCarConfig = carConfig || {}
  }

  public setRoute(route: Route2g) {
    if (
      route.stops.filter(
        (stop) =>
          (isStopActive(stop) && stop.proximity_status !== "arrived") ||
          stop.is_temporary,
      ).length === 0
    ) {
      this.showCurrentLocationOrPastStops()
      this.onClearRoute?.()
      this.clearRoute()
      this.fetchRoute()
      return
    }
    const onlyActionsChanged = this.areOnlyActionsChanged(route)
    this.route = route
    this.directions?.setStops(route.stops)
    this.fetchRoute(onlyActionsChanged)
  }

  public getStops(): Route2g["stops"] {
    return this.route?.stops ?? []
  }

  public setRouteInfo(routeInfo: RouteInfo) {
    this.routeInfo = routeInfo
  }

  public setShouldShowEntireRoute(value: boolean) {
    this.shouldShowEntireRoute = value
  }

  public setSelectedRouteLabel(label: string | undefined) {
    this.selectedRouteLabel = label
  }

  private updateRouteLayerColors() {
    if (!this.mapInstance || !this.mapInstance.isStyleLoaded()) return

    const mainLayerId = "maplibre-gl-directions-routeline"
    const casingLayerId = "maplibre-gl-directions-routeline-casing"
    const whiteLayerId = "maplibre-gl-directions-routeline-white"

    try {
      if (this.selectedRouteLabel) {
        // Use three-layer colored styling
        const colors = getRouteColorsFromLabel(this.selectedRouteLabel)

        // Update or create white layer if it doesn't exist
        if (!this.mapInstance.getLayer(whiteLayerId)) {
          this.mapInstance.addLayer(
            {
              id: whiteLayerId,
              type: "line",
              source: "maplibre-gl-directions",
              layout: {
                "line-cap": "butt",
                "line-join": "round",
              },
              paint: {
                "line-color": "#FFFFFF",
                "line-opacity": 1,
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  10,
                  7,
                  16,
                  11,
                ],
              },
              filter: ["==", ["get", "route"], "SELECTED"],
            },
            mainLayerId,
          )
        } else {
          this.mapInstance.setPaintProperty(
            whiteLayerId,
            "line-color",
            "#FFFFFF",
          )
          this.mapInstance.setPaintProperty(whiteLayerId, "line-opacity", 1)
        }

        // Update casing to blue
        this.mapInstance.setPaintProperty(
          casingLayerId,
          "line-color",
          "#2563EB",
        )
        this.mapInstance.setPaintProperty(casingLayerId, "line-opacity", 1)
        this.mapInstance.setPaintProperty(casingLayerId, "line-width", [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          9,
          16,
          13,
        ])

        // Update main line to route color
        this.mapInstance.setPaintProperty(
          mainLayerId,
          "line-color",
          colors.main,
        )
        this.mapInstance.setPaintProperty(mainLayerId, "line-opacity", 1)
        this.mapInstance.setPaintProperty(mainLayerId, "line-width", [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          5,
          16,
          9,
        ])
      } else {
        // Remove white layer if it exists
        if (this.mapInstance.getLayer(whiteLayerId)) {
          this.mapInstance.removeLayer(whiteLayerId)
        }

        // Restore default styling (black casing, congestion colors)
        this.mapInstance.setPaintProperty(
          casingLayerId,
          "line-color",
          "#000000",
        )
        this.mapInstance.setPaintProperty(casingLayerId, "line-opacity", 1)
        this.mapInstance.setPaintProperty(casingLayerId, "line-width", [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          6,
          16,
          10,
        ])

        this.mapInstance.setPaintProperty(
          mainLayerId,
          "line-color",
          ROUTE_CONGESTION_COLOR,
        )
        this.mapInstance.setPaintProperty(mainLayerId, "line-opacity", 0.85)
        this.mapInstance.setPaintProperty(mainLayerId, "line-width", [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          4,
          16,
          8,
        ])
      }
    } catch (error) {
      console.error("Error updating route layer colors:", error)
    }
  }

  public clearRoute() {
    this.removeReferenceRouteFromMap()
    this.removeDriverPolylineFromMap()
    this.route = null
    this.routeInfo = null
    this.sections = null
    this.polylines = null
    this.updateTrafficIncidents()
    if (!this.isAlertMap) {
      this.shouldShowEntireRoute = true
    }
    if (this.distancePopup) {
      this.distancePopup.off("close", this.handleDistancePopupClose)
      this.distancePopup.remove()
    }
    // Clear route visualization from map for all map types
    this.directions?.clearRoute()
  }

  // Remove reference route from the map
  private removeReferenceRouteFromMap() {
    if (!this.mapInstance) return

    try {
      // Check if style is loaded before trying to remove layers/sources
      if (!this.mapInstance.isStyleLoaded()) {
        return // Skip if style isn't loaded
      }

      if (this.mapInstance.getLayer("reference-route-layer")) {
        this.mapInstance.removeLayer("reference-route-layer")
      }
      if (this.mapInstance.getSource("reference-route-source")) {
        this.mapInstance.removeSource("reference-route-source")
      }
    } catch (err) {
      console.error("Error removing reference route:", err)
    }
  }

  // Remove driver polyline from the map
  private removeDriverPolylineFromMap() {
    if (!this.mapInstance) return

    try {
      if (this.mapInstance?.getLayer("driver-route-layer")) {
        this.mapInstance?.removeLayer("driver-route-layer")
      }
      if (this.mapInstance?.getSource("driver-route-source")) {
        this.mapInstance?.removeSource("driver-route-source")
      }
    } catch (err) {
      console.error("Error removing driver polyline:", err)
    }
  }

  private areOnlyActionsChanged(route: Route2g) {
    return (
      this.route?.stops &&
      route?.stops &&
      route.stops.length > 0 &&
      this.route?.stops?.length === route.stops?.length &&
      this.route?.stops.some(
        (stop, index) => stop.action !== route.stops[index]?.action,
      )
    )
  }

  public setCurrentLocation(
    location: ILngLatLike,
    activity?: string | undefined,
    bearing?: number | undefined,
  ) {
    this.currentLocation = location
    this.currentActivity = activity
    this.currentLocationBearing = bearing
    const locationPoint = this.mapInstance?.getSource(
      "location-point",
    ) as GeoJSONSource
    if (locationPoint) {
      locationPoint.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: this.currentLocation as number[],
            },
            properties: {
              bearing: bearing,
              activity: activity,
            },
          },
        ],
      })
    }
  }

  public getCurrentLocation(): ILngLatLike | null {
    return this.currentLocation
  }

  /**
   * With no route left to draw, the map used to fly to the driver. When the
   * past-stops overlay is on, the stops already done are what the dispatcher
   * is looking at, so fit them together with the driver instead.
   */
  private showCurrentLocationOrPastStops() {
    if (pastStopsOverlayPoints(this.pastStopsOverlay).length > 0) {
      this.showEntireRoute()
      return
    }
    this.showCurrentLocation()
  }

  public showCurrentLocation() {
    if (!this.currentLocation || !this.mapInstance) return
    this.mapInstance.flyTo({
      center: this.currentLocation,
      essential: true,
      zoom: 10,
      duration: 1000,
    })
  }

  /** Fly the map to an arbitrary coordinate. */
  public flyToCoordinate(lng: number, lat: number, zoom: number = 14) {
    if (!this.mapInstance) return
    this.mapInstance.flyTo({
      center: [lng, lat],
      essential: true,
      zoom,
      duration: 1000,
    })
  }

  public setOnRouteStopsUpdate(
    callback: (route: Route2g["stops"]) => Promise<void>,
  ) {
    this.onRouteStopsUpdate = callback
  }

  public setOnRouteDragChange(
    callback: ((isDragging: boolean) => void) | undefined,
  ) {
    this.onRouteDragChange = callback
    this.directions?.setOnRouteDragChange(callback ?? null)
  }

  public getCurrentMapStyle(): MapStyle {
    return this.mapConfig.style as MapStyle
  }

  public async addStopAsDestination(
    clickPosition: [number, number],
    action: string = "other",
    locationInfo?: Pick<Location, "formattedAddress" | "companyName">,
  ) {
    try {
      this.isUpdatingRoute = true
      const stops = this.route?.stops ? this.enrichStops(this.route.stops) : []

      const newStop = {
        location: {
          ...locationInfo,
          formattedAddress:
            locationInfo?.formattedAddress ||
            `${clickPosition[1]},${clickPosition[0]}`,
          lat: clickPosition[1],
          lon: clickPosition[0],
        },
        addressUpdated: true,
        action: action,
        is_temporary: this.isAnalyzeOrderMap,
      } as unknown as RouteStop

      // Add as final destination
      const updated = [...(stops || []), newStop]

      try {
        await this.onRouteStopsUpdate?.(updated)
      } catch (e) {
        this.fetchRoute(true)
      }
    } finally {
      this.isUpdatingRoute = false
    }
  }

  public async addStopAsPassthrough(
    clickPosition: [number, number],
    afterStopIndex: number,
    locationInfo?: Pick<Location, "formattedAddress" | "companyName">,
  ) {
    try {
      this.isUpdatingRoute = true
      const stops = this.route?.stops ? this.enrichStops(this.route.stops) : []

      const newStop = {
        location: {
          ...locationInfo,
          formattedAddress:
            locationInfo?.formattedAddress ||
            `${clickPosition[1]},${clickPosition[0]}`,
          lat: clickPosition[1],
          lng: clickPosition[0],
        },
        addressUpdated: true,
        action: "passthrough",
        is_temporary: this.isAnalyzeOrderMap,
      } as unknown as RouteStop

      // Insert the passthrough stop after the specified stop index
      const insertIndex = afterStopIndex + 1
      const updated = [
        ...(stops || []).slice(0, insertIndex),
        newStop,
        ...(stops || []).slice(insertIndex),
      ]

      try {
        await this.onRouteStopsUpdate?.(updated)
      } catch (e) {
        this.fetchRoute(true)
      }
    } finally {
      this.isUpdatingRoute = false
    }
  }

  /**
   * Insert a stop with an explicit action after the given stop index.
   *
   * Unlike addStopAsDestination (which always appends) and addStopAsPassthrough
   * (which hardcodes the "passthrough" action), this lets the caller place a
   * stop of any action type at any position. Pass afterStopIndex = -1 to make
   * it the first stop, immediately after the current driver location.
   */
  public async addStopAfterIndex(
    clickPosition: [number, number],
    afterStopIndex: number,
    action: string = "other",
    locationInfo?: Pick<Location, "formattedAddress" | "companyName">,
  ) {
    try {
      this.isUpdatingRoute = true
      const stops = this.route?.stops ? this.enrichStops(this.route.stops) : []

      const newStop = {
        location: {
          ...locationInfo,
          formattedAddress:
            locationInfo?.formattedAddress ||
            `${clickPosition[1]},${clickPosition[0]}`,
          lat: clickPosition[1],
          lon: clickPosition[0],
        },
        addressUpdated: true,
        action: action,
        is_temporary: this.isAnalyzeOrderMap,
      } as unknown as RouteStop

      // Insert the new stop after the specified stop index
      const insertIndex = afterStopIndex + 1
      const updated = [
        ...(stops || []).slice(0, insertIndex),
        newStop,
        ...(stops || []).slice(insertIndex),
      ]

      try {
        await this.onRouteStopsUpdate?.(updated)
      } catch (e) {
        this.fetchRoute(true)
      }
    } finally {
      this.isUpdatingRoute = false
    }
  }

  public async removeStop(stopIndex: number) {
    if (!this.route?.stops) return

    try {
      this.isUpdatingRoute = true
      const stops = this.enrichStops(this.route.stops)
      if (!stops || stopIndex < 0 || stopIndex >= stops.length) return

      const updated = [
        ...stops.slice(0, stopIndex),
        ...stops.slice(stopIndex + 1),
      ]

      try {
        await this.onRouteStopsUpdate?.(updated)
      } catch (e) {
        this.fetchRoute(true)
      }
    } finally {
      this.isUpdatingRoute = false
    }
  }

  private handleDistancePopupClose = () => {
    this.distancePopupDismissed = true
  }

  private createRouteInfoPopup(
    position: ILngLatLike,
    data: {
      totalDuration: number
      totalDistance: number
      polylines: string[]
    },
  ) {
    if (this.distancePopup) {
      this.distancePopup.off("close", this.handleDistancePopupClose)
      this.distancePopup.remove()
    }

    if (this.distancePopupDismissed) {
      return
    }

    if (!this.mapInstance || !this.directions) return

    const differenceFromNow = (time: string | undefined) => {
      if (!time) return 0
      return Math.max(
        Math.floor(new Date(time).getTime() - new Date().getTime()) / 1000,
        0,
      )
    }

    const isAnalyzeOrderEstimate =
      this.isAnalyzeOrderMap &&
      (!this.currentLocation ||
        this.routeInfo?.routes[0].isCustom ||
        this.routeInfo?.routes[0].isAlternative)
    const estimatedArrivalTimeWithTacho =
      this.route?.stops?.at(-1)?.estimated_arrival_time_with_tacho
    const popupHtml = () =>
      isAnalyzeOrderEstimate
        ? analyzeOrderRoutePopupHtml({
            driveSeconds: data.totalDuration,
            distanceMeters: data.totalDistance,
          })
        : routeInfoPopupHtml({
            driveSeconds: differenceFromNow(
              this.route?.stops?.at(-1)?.estimated_arrival_time,
            ),
            withBreaksSeconds: estimatedArrivalTimeWithTacho
              ? differenceFromNow(estimatedArrivalTimeWithTacho)
              : null,
            trafficDelay: shownTrafficDelay(this.routeInfo?.routes[0]?.legs, {
              colors: this.routeTraffic.colors,
              trafficFetchedAt: this.routeInfo?.traffic_fetched_at,
            }),
            distanceMeters: data.totalDistance,
            trafficFetchedAt: this.routeInfo?.traffic_fetched_at,
          })

    const popup = new Popup({
      closeButton: true,
      closeOnClick: false,
      focusAfterOpen: false,
      className: "z-10",
    })
      .setLngLat(position)
      .setHTML(popupHtml())
      .addTo(this.mapInstance)
    this.distancePopup = popup
    popup.on("close", this.handleDistancePopupClose)
    this.redrawRouteInfoPopup = redrawEveryMinute(popup, popupHtml, () =>
      this.placeDistancePopup(),
    )
    this.placeDistancePopup()
  }

  public placeDistancePopup() {
    if (!this.mapInstance || !this.distancePopup) return
    const margin = 8
    const container = this.mapInstance.getContainer()
    const map = container.getBoundingClientRect()
    const popupElement = this.distancePopup.getElement()
    if (!popupElement) return
    this.distancePopup.setMaxWidth(`${map.width - 2 * margin}px`)
    popupElement.style.translate = ""
    const obstacles = Array.from(
      container.querySelectorAll(".maplibregl-ctrl, .maplibregl-popup"),
    )
      .filter((element) => element !== popupElement)
      .map((element) => ({
        box: element.getBoundingClientRect(),
        weight: element.classList.contains("maplibregl-popup") ? 10 : 1,
      }))
      .filter(({ box }) => box.width > 0 && box.height > 0)
    const { dx, dy } = popupShift(
      popupElement.getBoundingClientRect(),
      {
        left: map.left + margin,
        top: map.top + margin,
        right: map.right - margin,
        bottom: map.bottom - margin,
      },
      obstacles,
    )
    popupElement.style.translate = dx || dy ? `${dx}px ${dy}px` : ""
  }

  private updateDistancePopupPositionDebounced = debounce(() => {
    if (!this.mapInstance || !this.polylines || !this.distancePopup) return
    const newPosition = this.directions?.findVisibleRouteCenter(this.polylines)
    if (newPosition) {
      this.distancePopup.setLngLat(newPosition)
      this.placeDistancePopup()
    }
  }, 100)

  private updateDistancePopupPosition = () => {
    this.updateDistancePopupPositionDebounced()
  }

  private createActionIcons() {
    const customIcons = [
      {
        id: "lucide-other",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-icon lucide-map-pin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" fill="white"/><circle cx="12" cy="10" r="3" fill="white"/></svg>',
      },
      {
        id: "lucide-waypoint",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin-plus-inside-icon lucide-map-pin-plus-inside"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" fill="white"/><path d="M12 7v6"/><path d="M9 10h6"/></svg>',
      },
      {
        id: "lucide-passthrough",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-milestone-icon lucide-milestone"><path d="M4 6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h13a2 2 0 0 0 1.152-.365l3.424-2.317a1 1 0 0 0 0-1.635l-3.424-2.318A2 2 0 0 0 17 6z" fill="white"/><path d="M12 13v8"/><path d="M12 3v3"/></svg>',
      },
      {
        id: "lucide-customs",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bookmark-check-icon lucide-bookmark-check"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2Z" fill="white"/><path d="m9 10 2 2 4-4"/></svg>',
      },
      {
        id: "lucide-ferry",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-ship-icon lucide-ship"><path d="M12 10.189V14"/><path d="M12 2v3"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" fill="white"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76" fill="white"/><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></svg>',
      },
      {
        id: "lucide-parking",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-parking-icon lucide-square-parking"><rect width="18" height="18" x="3" y="3" rx="2" fill="white"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></svg>',
      },
      {
        id: "lucide-refuel",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-fuel-icon lucide-fuel"><line x1="3" x2="15" y1="22" y2="22"/><rect x="4" y="2" width="10" height="20" rx="2" ry="2" fill="white" stroke="none"/><line x1="4" x2="14" y1="9" y2="9" stroke="currentColor" stroke-width="2"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5"/></svg>',
      },
      {
        id: "lucide-delivery",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-package-check-icon lucide-package-check"><path d="m16 16 2 2 4-4"/><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" fill="white"/><path d="m7.5 4.27 9 5.15"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" x2="12" y1="22" y2="12"/></svg>',
      },
      {
        id: "lucide-pickup",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart-icon lucide-shopping-cart"><circle cx="8" cy="21" r="1" fill="white"/><circle cx="19" cy="21" r="1" fill="white"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" fill="white"/></svg>',
      },
      {
        id: "lucide-truck",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-truck-icon lucide-truck"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" fill="white"/><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" fill="white"/> <circle cx="17" cy="18" r="2" fill="white"/> <circle cx="7" cy="18" r="2" fill="white"/></svg>',
      },
      {
        id: "lucide-scale",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="white" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-scale-icon lucide-scale"><path d="M12 3v18"/><path d="m19 8 3 8a5 5 0 0 1-6 0zV7"/><path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1"/><path d="m5 8 3 8a5 5 0 0 1-6 0zV7"/><path d="M7 21h10"/></svg>',
      },
      {
        id: "lucide-drop-off",
        svg: '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line-icon lucide-arrow-down-to-line"><rect x="1" y="1" width="22" height="22" rx="4" ry="4" fill="white" stroke="currentColor" stroke-width="2"/><path d="M12 15V6"/><path d="m8 12 4 4 4-4"/><path d="M17 19H7"/></svg>',
      },
    ]

    // Add custom map icons safely
    for (const icon of customIcons) {
      if (!this.mapInstance.hasImage(icon.id)) {
        this.mapInstance.addImage(
          icon.id,
          svgToCanvas(this.mapInstance, icon.svg),
          { pixelRatio: 2 },
        )
      }
    }
  }

  private createDefaultRouteLayers(): LayerSpecification[] {
    // Default route layers with congestion colors (used in normal route view)
    return [
      {
        id: "maplibre-gl-directions-routeline-casing",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#000000",
          "line-opacity": 1,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 6, 16, 10],
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
      {
        id: "maplibre-gl-directions-routeline",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": ROUTE_CONGESTION_COLOR,
          "line-opacity": 0.85,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 4, 16, 8],
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
    ] as LayerSpecification[]
  }

  private createAnalyzeOrderRouteLayers(): LayerSpecification[] {
    // Three-layer route styling for analyze order mode with route type colors
    const colors = getRouteColorsFromLabel(this.selectedRouteLabel)

    return [
      // Blue outer casing layer
      {
        id: "maplibre-gl-directions-routeline-casing",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
        paint: {
          "line-color": "#2563EB", // Blue
          "line-opacity": 1,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 9, 16, 13],
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
      // White middle layer
      {
        id: "maplibre-gl-directions-routeline-white",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
        paint: {
          "line-color": "#FFFFFF", // White
          "line-opacity": 1,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 7, 16, 11],
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
      // Route type color main line
      {
        id: "maplibre-gl-directions-routeline",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
        paint: {
          "line-color": colors.main, // Route type color
          "line-opacity": 1,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 9],
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
    ] as LayerSpecification[]
  }

  private createLayerIcons(): LayerSpecification[] {
    const layers = [
      {
        id: "maplibre-gl-directions-snapline",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-dasharray": [3, 3],
          "line-color": "#34343f",
          "line-opacity": 0.65,
          "line-width": 3,
        },
        filter: ["==", ["get", "type"], "SNAPLINE"],
      },
      {
        id: "maplibre-gl-directions-alt-routeline-casing",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
        paint: {
          "line-color": "#808080",
          "line-opacity": 0.3,
          "line-width": [
            "interpolate",
            ["exponential", 1.5],
            ["zoom"],
            0,
            ["case", ["boolean", ["get", "highlight"], false], 10, 7],
            5,
            ["case", ["boolean", ["get", "highlight"], false], 10, 7],
            18,
            ["case", ["boolean", ["get", "highlight"], false], 32, 23],
          ],
        },
        filter: ["==", ["get", "route"], "ALT"],
      },
      {
        id: "maplibre-gl-directions-alt-routeline",
        type: "line",
        source: "maplibre-gl-directions",
        layout: {
          "line-cap": "butt",
          "line-join": "round",
        },
        paint: {
          "line-color": "#A0A0A0",
          "line-opacity": 0.6,
          "line-width": [
            "interpolate",
            ["exponential", 1.5],
            ["zoom"],
            0,
            3,
            5,
            3,
            18,
            10,
          ],
        },
        filter: ["==", ["get", "route"], "ALT"],
      },
      // Conditionally add route line layers based on mode
      ...(this.isAnalyzeOrderMap && this.selectedRouteLabel
        ? this.createAnalyzeOrderRouteLayers()
        : this.createDefaultRouteLayers()),
      {
        id: "maplibre-gl-directions-hoverpoint-casing",
        type: "circle",
        source: "maplibre-gl-directions",
        paint: {
          "circle-radius": 8,
          "circle-color": "#00CC00",
          "circle-opacity": 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1,
        },
        filter: ["==", ["get", "type"], "HOVERPOINT"],
      },
      {
        id: "maplibre-gl-directions-hoverpoint",
        type: "circle",
        source: "maplibre-gl-directions",
        paint: {
          "circle-radius": 8,
          "circle-color": "#00CC00",
          "circle-opacity": 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1,
        },
        filter: ["==", ["get", "type"], "HOVERPOINT"],
      },
      {
        id: "maplibre-gl-directions-snappoint-casing",
        type: "circle",
        source: "maplibre-gl-directions",
        paint: {
          "circle-radius": 0,
          "circle-color": "#FFFFFF",
          "circle-opacity": 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 2,
        },
        filter: ["==", ["get", "type"], "SNAPPOINT"],
      },
      {
        id: "maplibre-gl-directions-snappoint",
        type: "circle",
        source: "maplibre-gl-directions",
        paint: {
          "circle-radius": 0,
          "circle-color": "#FFFFFF",
          "circle-opacity": 1,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 2,
        },
        filter: ["==", ["get", "type"], "SNAPPOINT"],
      },
      {
        id: "maplibre-gl-directions-route-arrows",
        type: "symbol",
        source: "maplibre-gl-directions",
        layout: {
          "symbol-placement": "line",
          "symbol-spacing": 100,
          "icon-image": "arrow",
          "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 16, 0.8],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-padding": 0,
          "icon-rotation-alignment": "map",
        },
        filter: ["==", ["get", "route"], "SELECTED"],
      },
    ] as LayerSpecification[]

    const allActions = [...ACTIONS]

    const iconExpressions: any = [
      "match",
      ["get", "action"],
      ...allActions.flatMap((action) => [
        action.value,
        `lucide-${action.value}`,
      ]),
      "lucide-other",
    ]

    // Add our custom icon layer
    layers.push({
      id: "maplibre-gl-directions-waypoint-icon",
      type: "symbol",
      source: "maplibre-gl-directions",
      layout: {
        "icon-image": iconExpressions,
        "icon-size": 1.0,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      filter: [
        "all",
        ["==", ["geometry-type"], "Point"],
        ["==", ["get", "type"], "WAYPOINT"],
        ["!=", ["get", "action"], "location"],
        ["!=", ["get", "action"], "from_driver_location"], // exclude driver
      ],
    })

    layers.push(stopNumberLayer(this.showStopNumbers))

    // Add our custom icon layer
    layers.push({
      id: "maplibre-gl-directions-waypoint-driver-icon",
      type: "symbol",
      source: "maplibre-gl-directions",
      layout: {
        "icon-image": "driver-icon",
      },
      filter: [
        "all",
        ["==", ["geometry-type"], "Point"],
        ["==", ["get", "type"], "WAYPOINT"],
        ["==", ["get", "action"], "from_driver_location"], // exclude driver
      ],
    })

    return layers
  }

  public removeAllPopups(): void {
    const popupElements = document.querySelectorAll(".maplibregl-popup")
    popupElements.forEach((element) => {
      element.remove()
    })
  }
}
